import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, ShieldCheck, Star, AlertTriangle, Camera, ChevronDown, ChevronUp, FileDown, PenLine, MoreVertical, Trash2, Share2, Copy, Download, Loader, AlertCircle } from "lucide-react";
import { api, API, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { redirectToCheckout } from "../lib/payment_helpers";
import OFix from "./OFix";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import PhotoUpload from "../components/PhotoUpload";
import SigningFlowWizard from "../components/SigningFlowWizard";
import AgreementPreviewCard from "../components/AgreementPreviewCard";
import { MediaGrid } from "../components/MediaLightbox";
import FeedbackModal, { hasFeedback } from "../components/FeedbackModal";

const COND = ["excellent", "good", "fair", "damaged"];

function TabBtn({ active, children, onClick, testid, stageStatus }) {
  const locked = stageStatus === "locked";
  const complete = stageStatus === "complete";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={locked}
      title={locked ? "Complete the previous stage to unlock this one" : undefined}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap ${
        active
          ? "border-indigo-500 text-[#F97316]"
          : locked
          ? "border-transparent text-gray-300 cursor-not-allowed"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {complete && <Check className="h-3.5 w-3.5 text-emerald-500" />}
      {locked && <Lock className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}

function SignatureRow({ label, signed, at, onResend, isResending }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-700">{label}</span>
      <div className="flex items-center gap-2">
        {signed
          ? <span className="pill bg-emerald-50 text-emerald-700 flex items-center gap-1"><Check className="h-3 w-3" /> Signed {at && `· ${formatDate(at)}`}</span>
          : <span className="pill bg-amber-50 text-amber-700">Waiting</span>}
        {!signed && onResend && (
          <button
            onClick={onResend}
            disabled={isResending}
            className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition disabled:opacity-50"
          >
            {isResending ? '...' : 'Resend'}
          </button>
        )}
      </div>
    </div>
  );
}

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button type="button" key={n} onClick={() => onChange(n)} className="p-0.5">
          <Star className={`h-6 w-6 ${n <= value ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
}

// Condition colour helper
function condPill(c) {
  return c === "excellent" ? "bg-emerald-50 text-emerald-700"
    : c === "good" ? "bg-blue-50 text-blue-700"
    : c === "fair" ? "bg-amber-50 text-amber-700"
    : "bg-red-50 text-red-700";
}

// ── Brokerage (dalali) ──
// The broker's own fee, collected through Orello. Orello takes 0% — the whole
// amount lands in the broker's wallet, withdrawable immediately. Three views in
// one: the creator sets/waives the amounts, the tenant/landlord pays their own
// share, and everyone sees the receipts.
function BrokerageSection({ rental, brokerage, receipts, isCreator, busy, onPay, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [fromTenant, setFromTenant] = useState("");
  const [fromLandlord, setFromLandlord] = useState("");
  const [saving, setSaving] = useState(false);

  if (!brokerage) return null;

  const isSet = brokerage.brokerage_set;
  const activatable = ["onboarding_pending", "agreement_pending", "payment_pending"].includes(rental.status);
  // Nothing to show: no brokerage set, and no one who could still set it.
  if (!isSet && !(isCreator && activatable)) return null;
  // A party who owes nothing has no business seeing this section — rendering an
  // empty version of it would itself hint that the other side is being charged.
  if (isSet && !isCreator && !brokerage.amount_owed_paise) return null;

  const save = async () => {
    const t = Math.round(Number(fromTenant || 0) * 100);
    const l = Math.round(Number(fromLandlord || 0) * 100);
    if (t < 0 || l < 0 || Number.isNaN(t) || Number.isNaN(l)) {
      toast.error("Enter valid amounts.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/rentals/${rental.id}/brokerage`, {
        brokerage_from_tenant: t,
        brokerage_from_landlord: l,
      });
      toast.success("Brokerage saved. Both parties have been notified.");
      setEditing(false);
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const waive = async () => {
    if (!window.confirm("Mark brokerage as settled outside Orello? This unblocks the tenancy without an online payment.")) return;
    setSaving(true);
    try {
      await api.post(`/rentals/${rental.id}/brokerage/waive`, { reason: "Settled outside Orello" });
      toast.success("Brokerage marked as settled.");
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-700">Brokerage</div>
        {isCreator && isSet && !brokerage.settled && (
          <button onClick={waive} disabled={saving} className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50">
            Settled offline?
          </button>
        )}
      </div>

      {/* Creator: set the amounts */}
      {isCreator && activatable && (!isSet || editing) && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Orello collects this from the parties and passes on 100% — no platform cut. It lands in your wallet, withdrawable immediately.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">From tenant (₹)</label>
              <input type="number" min="0" value={fromTenant} onChange={(e) => setFromTenant(e.target.value)}
                placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">From landlord (₹)</label>
              <input type="number" min="0" value={fromLandlord} onChange={(e) => setFromLandlord(e.target.value)}
                placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            ⚠️ The tenancy won't go active until any brokerage you set here is paid (or marked settled offline).
          </p>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? "Saving…" : "Save brokerage"}
            </button>
            {editing && (
              <button onClick={() => setEditing(false)} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Broker only: the full split, since they set it and receive both sides.
          A tenant/landlord must never see what the other party is charged. */}
      {isSet && !editing && isCreator && (
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">From tenant</span>
            <span className="font-medium">{formatINR(brokerage.from_tenant_paise)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">From landlord</span>
            <span className="font-medium">{formatINR(brokerage.from_landlord_paise)}</span></div>
          {brokerage.waived && <div className="text-xs text-gray-500 pt-1">Marked settled outside Orello.</div>}
        </div>
      )}

      {/* Tenant / landlord: only their own share and who it goes to. */}
      {isSet && !editing && !isCreator && brokerage.role && (
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Your brokerage</span>
            <span className="font-medium">{formatINR(brokerage.amount_owed_paise)}</span></div>
          {brokerage.recipient_name && (
            <div className="flex justify-between"><span className="text-gray-500">Payable to</span>
              <span className="font-medium">{brokerage.recipient_name}</span></div>
          )}
          {brokerage.waived && <div className="text-xs text-gray-500 pt-1">Marked settled outside Orello.</div>}
        </div>
      )}

      {/* Tenant / landlord: pay your own share */}
      {brokerage.requires_payment && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-800">You owe {formatINR(brokerage.amount_owed_paise)}</div>
            <div className="text-xs text-gray-500">Paid securely through Orello — no platform fee added.</div>
          </div>
          <button onClick={onPay} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap disabled:opacity-50">
            Pay now
          </button>
        </div>
      )}
      {brokerage.role && brokerage.amount_owed_paise > 0 && brokerage.already_paid && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          ✓ You've paid your brokerage share.
        </div>
      )}

      {/* Everyone: receipts */}
      {receipts.length > 0 && (
        <div className="pt-1">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Receipts</div>
          {receipts.map((rc) => (
            <div key={rc.id} className="flex items-center justify-between text-xs bg-white border border-gray-100 rounded-lg px-3 py-2 mb-1">
              <div>
                <div className="text-gray-700">{rc.receipt_number}</div>
                <div className="text-gray-500 capitalize">Paid by {rc.payer_role} · {formatDate(rc.created_at)}</div>
              </div>
              <span className="font-medium text-gray-800">{formatINR(rc.amount_paise)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Input room card (used when submitting checkin / checkout report) ──
function RoomCard({ room, value, onChange, showNotes = true, showOfix = true, lockedNa = false }) {
  const [open, setOpen] = useState(false);
  const photos = value?.photos || [];
  // lockedNa: this section was marked "Not in this property" at check-in, so it
  // can't exist at checkout either — force-exclude it and lock the toggle.
  const na = lockedNa || !!value?.not_applicable;
  const isComplete = na || (value?.condition && value?.notes?.trim() && photos.length > 0);
  return (
    <div className={`card-base p-4 ${open && !isComplete ? "border-amber-300" : ""}`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          <div className="font-medium text-sm">{room}</div>
          {!isComplete && <span className="text-xs text-amber-500">*required</span>}
        </div>
        <div className="flex items-center gap-2">
          {na ? (
            <span className="pill text-xs bg-gray-100 text-gray-500">Not applicable</span>
          ) : (
            <>
              {value?.condition && (
                <span className={`pill text-xs ${condPill(value.condition)}`}>{value.condition}</span>
              )}
              {showOfix && value?.add_to_ofix && (
                <span className="pill text-xs bg-orange-50 text-orange-700 border border-orange-200">O-Fix</span>
              )}
              {photos.length > 0 && <span className="text-xs text-gray-400">{photos.length} photo{photos.length > 1 ? "s" : ""}</span>}
            </>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-4 border-t pt-4">
          {/* Not in this property — excludes the section (e.g. no furniture in an unfurnished flat) */}
          <label className={`flex items-start gap-2.5 bg-gray-50 border border-gray-200 rounded-lg p-3 ${lockedNa ? "cursor-default opacity-90" : "cursor-pointer"}`}>
            <input
              type="checkbox"
              checked={na}
              disabled={lockedNa}
              onChange={(e) => onChange({ ...value, not_applicable: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-800">Not in this property</div>
              <div className="text-xs text-gray-500">
                {lockedNa
                  ? "Excluded at check-in — this section isn't part of the property, so no checkout condition or photos are needed."
                  : "Tick if this section doesn't exist here (e.g. no furniture in an unfurnished flat). It'll be excluded — no condition or photos needed."}
              </div>
            </div>
          </label>

          {!na && (<>
          {/* Condition — mandatory */}
          <div>
            <div className="flex items-center gap-1 mb-2">
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Condition</span>
              <span className="text-xs text-red-500">*</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {COND.map(c => (
                <button type="button" key={c} onClick={() => onChange({ ...value, condition: c })}
                  className={`py-2.5 rounded-lg text-xs font-medium border capitalize transition-colors ${value?.condition === c ? "bg-[#FFF4ED] border-indigo-500 text-indigo-900" : "border-gray-200 text-gray-500 hover:border-indigo-300"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Notes — mandatory */}
          {showNotes && (
            <div>
              <div className="flex items-center gap-1 mb-1.5">
                <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Notes</span>
                <span className="text-xs text-red-500">*</span>
              </div>
              <textarea
                value={value?.notes || ""}
                onChange={(e) => onChange({ ...value, notes: e.target.value })}
                placeholder="Describe the condition of this room in detail. Note any damage, marks, or anything that differs from normal. This becomes evidence in any future dispute."
                className={`w-full border rounded-lg px-3 py-2 text-sm resize-none ${value?.notes?.trim() ? "border-gray-200" : "border-amber-300"}`}
                rows={3}
              />
            </div>
          )}

          {/* Photos — mandatory */}
          <div>
            <div className="flex items-center gap-1 mb-1.5">
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Photos / Videos</span>
              <span className="text-xs text-red-500">*</span>
            </div>
            <PhotoUpload value={photos} onChange={(p) => onChange({ ...value, photos: p })} label="Add photos or videos — at least 1 required" />
            {photos.length === 0 && (
              <div className="text-xs text-amber-600 mt-1">At least one photo is required per room</div>
            )}
          </div>

          {/* Add to O-Fix — check-in only (flag for the landlord to fix during tenancy) */}
          {showOfix && !na && (
            <label className="flex items-start gap-2.5 cursor-pointer bg-orange-50/40 border border-orange-100 rounded-lg p-3">
              <input
                type="checkbox"
                checked={!!value?.add_to_ofix}
                onChange={(e) => onChange({ ...value, add_to_ofix: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#F97316]"
              />
              <div>
                <div className="text-sm font-medium text-gray-800">Add to O-Fix</div>
                <div className="text-xs text-gray-500">Flag this room for the landlord to fix during the tenancy. It keeps the condition rating above and is tracked in the O-Fix phase.</div>
              </div>
            </label>
          )}

          {/* Evidence clause */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
            📋 These notes and photos are legally binding evidence. In case of any deposit dispute, Orello will use this information to make a fair and impartial decision. Ensure all details are accurate.
          </div>
          </>)}
        </div>
      )}
    </div>
  );
}

export default function RentalDetail() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Guard: redirect if rental ID is missing
  useEffect(() => {
    if (!rid || rid === "null") {
      navigate("/app");
    }
  }, [rid, navigate]);

  const [r, setR] = useState(null);
  const [brokerage, setBrokerage] = useState(null);
  const [brokerageReceipts, setBrokerageReceipts] = useState([]);
  const [tab, setTab] = useState("agreement");
  const [showClausesModal, setShowClausesModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showSigningWizard, setShowSigningWizard] = useState(false);

  // Lock background page scroll while the signing modal is open. Without this,
  // scrolling inside the modal past its own content boundary "leaks through" and
  // scrolls the page behind it instead (the reported scroll-chaining bug).
  useEffect(() => {
    if (!showSigningWizard) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [showSigningWizard]);

  const [termsAcceptedInLanguage, setTermsAcceptedInLanguage] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [depositShareInput, setDepositShareInput] = useState("");
  const [rooms, setRooms] = useState([]);
  const [deductionReasons, setDeductionReasons] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/rentals/${rid}`);
    setR(data);
    const { data: cats } = await api.get(`/rentals/${rid}/room-categories`);
    setRooms(cats.rooms || []);
  }, [rid]);

  // ── PHASE 7: Load deduction reasons ──
  useEffect(() => {
    const loadReasons = async () => {
      try {
        const { data } = await api.get("/deduction-reasons");
        setDeductionReasons(data.reasons || []);
      } catch (e) {
        console.error("Failed to load deduction reasons:", e);
      }
    };
    loadReasons();
  }, []);

  useEffect(() => { load(); }, [load]);

  // Brokerage state — server-computed per calling party (what they owe, whether
  // they've paid), plus any receipts issued. Failures are non-fatal: brokerage
  // is optional, so its absence must never block the rest of the page.
  const loadBrokerage = useCallback(async () => {
    if (!rid || rid === "null") return;
    try {
      const [status, receipts] = await Promise.all([
        api.get(`/rentals/${rid}/brokerage-status`),
        api.get(`/rentals/${rid}/brokerage-receipts`).catch(() => ({ data: { receipts: [] } })),
      ]);
      setBrokerage(status.data);
      setBrokerageReceipts(receipts.data?.receipts || []);
    } catch (e) {
      console.error("Failed to load brokerage status:", e);
    }
  }, [rid]);
  useEffect(() => { loadBrokerage(); }, [loadBrokerage]);

  if (!r) return <div className="text-gray-500 p-8">Loading…</div>;

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/rentals/${rid}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("orello_token")}` },
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orello-report-${rid.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF download failed — " + e.message); }
  };

  const handleSign = async ({ signature_image, name_typed, consent_text }) => {
    try {
      await api.post(`/rentals/${rid}/agreement/sign`, { signature_image, name_typed, consent_text });
      toast.success("Agreement signed successfully.");
      setShowSignModal(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payBrokerage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rentals/${rid}/payments/brokerage/order`);
      sessionStorage.setItem("orello_payment_order", data.order_id);
      sessionStorage.setItem("orello_payment_rental", rid);
      // Clear any leftover multi-step payment context — a stale breakdown would
      // make the return screen offer a bogus "next payment" after brokerage.
      ["orello_payment_breakdown", "orello_payment_step", "orello_payment_total_steps", "orello_agreement_id"]
        .forEach((k) => sessionStorage.removeItem(k));
      await redirectToCheckout(data.session_id, data.cashfree_mode);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start brokerage payment. Please try again.");
      setBusy(false);
    }
  };

  const payDeposit = async (tenantSharePaise) => {
    setBusy(true);
    try {
      const body = tenantSharePaise != null ? { tenant_share: tenantSharePaise } : {};
      const { data } = await api.post(`/rentals/${rid}/payments/deposit/order`, body);

      // Persist context for the return handler (/payment/status), matching the
      // agreement-fee / signing flows. On return, PaymentStatus polls
      // /payments/verify, which confirms with Cashfree and activates the deposit.
      sessionStorage.setItem("orello_payment_order", data.order_id);
      sessionStorage.setItem("orello_payment_rental", rid);

      // Hand the payment_session_id to Cashfree's SDK — this redirects the
      // browser to the hosted checkout and away from this page.
      await redirectToCheckout(data.session_id, data.cashfree_mode);
      // Execution stops here on success (browser navigates to Cashfree).
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start deposit payment. Please try again.");
      setBusy(false);
    }
  };

  const cancelAgreement = async () => {
    if (!window.confirm(`Cancel this agreement? All parties will be notified. This action cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${rid}/agreement/cancel`);
      toast.success("Agreement cancelled. All parties have been notified.");
      await new Promise(resolve => setTimeout(resolve, 1500));
      window.location.href = "/app";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestEarlyTermination = async () => {
    if (!window.confirm(`Request early termination? All parties will be notified. This starts the checkout process.`)) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${rid}/early-termination`);
      toast.success("Early termination requested. All parties have been notified.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deferCheckoutDeadline = async () => {
    if (!window.confirm(`Extend checkout deadline by 4 days? You can defer only once, for a total of 7 days.`)) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${rid}/defer-checkout-deadline`);
      toast.success("Checkout deadline extended by 4 days.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Share agreement link with parties
  const shareAgreement = async () => {
    const ag = r.agreement || {};
    const agreementId = ag.id || r.agreement_id;

    if (!agreementId) {
      console.error("Debug - Agreement object:", ag);
      console.error("Debug - Rental:", r);
      toast.error("Agreement ID not found. Agreement may not be created yet.");
      return;
    }

    const signingLink = `${window.location.origin}/agreements/${agreementId}/sign`;
    console.log("Sharing agreement link:", signingLink);

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(signingLink);
      toast.success("Agreement link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  // Download agreement PDF — works for the current agreement or any
  // historical (superseded-by-renewal) one, since both are downloadable by
  // their own agreement_id forever.
  const downloadPdfById = async (agreementId) => {
    if (!agreementId) {
      toast.error("Agreement ID not found");
      return;
    }
    try {
      setBusy(true);
      const response = await api.get(`/agreements/${agreementId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `agreement-${agreementId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Agreement PDF downloaded!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to download PDF");
    } finally {
      setBusy(false);
    }
  };
  const downloadAgreementPdf = () => downloadPdfById((r.agreement || {}).id);

  // Resend signing invitation
  const handleResendInvite = async () => {
    if (!r?.agreement?.id) return;
    try {
      setResendingId(r.agreement.id);
      const { data } = await api.post(`/agreements/${r.agreement.id}/resend-invite`);
      toast.success(data.message || "✅ Signing reminders sent!");
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to resend invite";
      toast.error(errorMsg);
      console.error("Error resending invite:", err);
    } finally {
      setResendingId(null);
    }
  };

  // Open delete confirmation
  const openDeleteConfirmation = () => {
    setShowDeleteConfirm(true);
  };

  // Delete agreement
  const handleDeleteAgreement = async () => {
    if (!r?.agreement?.id) return;
    try {
      setDeleting(true);
      const { data } = await api.delete(`/agreements/${r.agreement.id}`);
      toast.success(data.message || "✅ Agreement deleted successfully");
      setShowDeleteConfirm(false);
      // Redirect to home after deletion
      setTimeout(() => navigate("/app/home"), 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to delete agreement";
      toast.error(errorMsg);
      console.error("Error deleting agreement:", err);
    } finally {
      setDeleting(false);
    }
  };

  const ag = r.agreement || {};
  const isCreator = ["agent", "broker_admin"].includes(user.role) ||
    (user.role === "landlord" && user.self_managed && r.landlord_id === user.id);

  return (
    <div className="space-y-6" data-testid="rental-detail">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors"
        title="Go back"
      >
        ← Back
      </button>

      {/* E-Sign Agreements: 5-Step Signing Flow Wizard */}
      {/* PRODUCTION RULE: Only show signing flow to actual signatories (tenant/landlord) */}
      {/* Brokers, Agents, and Self-managed landlords should NOT sign */}
      {showSigningWizard && r && (() => {
        const isCreator = ["broker_admin", "agent"].includes(user?.role) ||
                         (user?.role === "landlord" && user?.self_managed);

        // Prevent creators from signing
        if (isCreator) {
          setShowSigningWizard(false);
          toast.error("You cannot sign this agreement as the creator. Only tenants and landlords can sign.");
          return null;
        }

        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl my-8">
              <SigningFlowWizard
                agreementId={r.agreement?.id}
                rentalId={r.id}
                userRole={user?.role}
                userName={user?.name}
                onSigningComplete={() => {
                  setShowSigningWizard(false);
                  // Reload rental data to show updated signature status
                  load();
                  toast.success("Agreement signed successfully!");
                }}
                onCancel={() => setShowSigningWizard(false)}
              />
            </div>
          </div>
        );
      })()}
      {r.status === "expired_continuing" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-amber-800">Agreement Expired — Tenancy Continuing</div>
            <p className="text-sm text-amber-700 mt-1">The lease date has passed but your deposit is safely held by Orello. Start checkout whenever you are ready.</p>
          </div>
        </div>
      )}

      {/* PHASE 5: Rent Tracking Disclaimer for Active Rentals */}
      {["active", "expired_continuing"].includes(r.status) && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <div className="text-blue-600 mt-0.5 flex-shrink-0">💬</div>
          <div>
            <div className="font-semibold text-blue-900">Rent Payment Tracking</div>
            <p className="text-sm text-blue-700 mt-1">Orello does not track rent payments. Landlord and tenant should use their preferred payment method and keep records. If a dispute arises about rent, Orello can help mediate but cannot verify payments made outside the platform.</p>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest">{r.property_type}</div>
          <h1 className="text-3xl font-bold font-display mt-1">{r.property_address}</h1>
          <div className="text-sm text-gray-500 mt-1">Lease {formatDate(r.lease_start_date)} → {formatDate(r.lease_end_date)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={r.status} />
          {/* Three-dot menu for agreement/active tenancy actions - only show for early termination */}
          {(["active", "expired_continuing"].includes(r.status) && (user.role === "agent" || user.role === "broker_admin" || (user.role === "landlord" && user.id === r.landlord_id))) && (
            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)} title="More options" className="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-lg transition-colors">
                <MoreVertical className="h-4 w-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {/* Early termination for active rentals only */}
                  {["active", "expired_continuing"].includes(r.status) && (user.role === "agent" || user.role === "broker_admin" || (user.role === "landlord" && user.id === r.landlord_id)) && (
                    <button
                      onClick={() => { setShowMenu(false); requestEarlyTermination(); }}
                      disabled={busy}
                      className="w-full text-left px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2 rounded-lg border-b border-gray-100"
                    >
                      <AlertTriangle className="h-3.5 w-3.5" /> Early Termination
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="card-base p-5"><div className="text-xs text-gray-500">Monthly rent</div><div className="text-xl font-bold mt-1">{formatINR(r.monthly_rent)}</div></div>
        <div className="card-base p-5"><div className="text-xs text-gray-500">Deposit</div><div className="text-xl font-bold mt-1">{formatINR(r.deposit_amount)}</div></div>
        <div className="card-base p-5"><div className="text-xs text-gray-500">Lock-in</div><div className="text-xl font-bold mt-1">{r.lock_in_months} months</div></div>
      </div>

      <div className="card-base">
        <div className="border-b border-gray-200 px-4 flex gap-2 overflow-x-auto">
          {(() => {
            const stageStatusOf = (key) => (r.stages || []).find((s) => s.key === key)?.status;
            const openTab = (key) => {
              if (stageStatusOf(key) === "locked") {
                toast.info("Complete the previous stage to unlock this one.");
                return;
              }
              setTab(key);
            };
            return (
              <>
                <TabBtn testid="tab-agreement" active={tab === "agreement"} stageStatus={stageStatusOf("agreement")} onClick={() => openTab("agreement")}>Agreement</TabBtn>
                {/* Tenancy stages exist with deposit protection OR the ₹1299 proof service.
                    The Deposit tab is only for deposit protection (Orello holds the money);
                    proof-service rentals get the report/dispute stages without a Deposit tab. */}
                {(r.deposit_protection || r.proof_service) && <>
                  {r.deposit_protection && <TabBtn testid="tab-deposit" active={tab === "deposit"} stageStatus={stageStatusOf("deposit")} onClick={() => openTab("deposit")}>Deposit</TabBtn>}
                  <TabBtn testid="tab-movein" active={tab === "movein"} stageStatus={stageStatusOf("movein")} onClick={() => openTab("movein")}>Start of Tenancy</TabBtn>
                  <TabBtn testid="tab-ofix" active={tab === "ofix"} stageStatus={stageStatusOf("ofix")} onClick={() => openTab("ofix")}>O-Fix</TabBtn>
                  <TabBtn testid="tab-moveout" active={tab === "moveout"} stageStatus={stageStatusOf("moveout")} onClick={() => openTab("moveout")}>End of Tenancy</TabBtn>
                  <TabBtn testid="tab-settlement" active={tab === "settlement"} stageStatus={stageStatusOf("settlement")} onClick={() => openTab("settlement")}>Settlement</TabBtn>
                </>}
                <TabBtn testid="tab-ratings" active={tab === "ratings"} stageStatus={stageStatusOf("ratings")} onClick={() => openTab("ratings")}>Ratings</TabBtn>
              </>
            );
          })()}
        </div>

        <div className="p-6">
          {tab === "agreement" && (
            <div className="space-y-5" data-testid="pane-agreement">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold font-display">Rental Agreement</h3>
                  <p className="text-sm text-gray-500">{ag.locked ? "Signed and locked." : "Read and sign your agreement."}</p>
                  {ag.id && (
                    <div className="mt-2 text-xs text-gray-500 font-mono">
                      ID: <span className="text-gray-700">{ag.id.slice(0, 12)}...</span>
                    </div>
                  )}
                </div>
                {/* Share and Download buttons */}
                {isCreator && !ag.locked && (
                  <div className="flex gap-2 flex-wrap justify-end">
                    <button
                      onClick={shareAgreement}
                      disabled={busy || !ag.id}
                      title="Copy shareable link to send to tenant/landlord"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Share2 className="h-3.5 w-3.5" /> Share
                    </button>
                    <button
                      onClick={downloadAgreementPdf}
                      disabled={busy || !ag.id}
                      title="Download agreement as PDF"
                      className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 border border-green-200 hover:border-green-300 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                    {ag.status === 'pending_signatures' && (
                      <button
                        type="button"
                        onClick={openDeleteConfirmation}
                        title="Delete unsigned agreement"
                        className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>

              {(r.agreement_history || []).length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Agreement History</div>
                  <div className="space-y-2">
                    {r.agreement_history.map((h) => (
                      <div key={h.agreement_id} className="flex items-center justify-between text-sm bg-white border border-gray-100 rounded-lg px-3 py-2">
                        <div>
                          <div className="text-gray-700">Superseded {formatDate(h.superseded_at)}</div>
                          <div className="text-xs text-gray-500">
                            Ended {formatDate(h.lease_end_date)} · ₹{((h.monthly_rent || 0) / 100).toLocaleString()}/mo
                          </div>
                        </div>
                        <button
                          onClick={() => downloadPdfById(h.agreement_id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 border border-green-200 hover:border-green-300 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Download className="h-3.5 w-3.5" /> PDF
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <BrokerageSection
                rental={r}
                brokerage={brokerage}
                receipts={brokerageReceipts}
                isCreator={isCreator}
                busy={busy}
                onPay={payBrokerage}
                onChanged={async () => { await load(); await loadBrokerage(); }}
              />

              {!ag.locked && (() => {
                const allTenantsSigned = ag.tenant_signatures && ag.tenant_signatures.every(sig => sig.signed_at);
                const landlordSigned = ag.signed_by_landlord;
                const needsSignature = !allTenantsSigned || !landlordSigned;

                return needsSignature && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <div className="text-amber-600 mt-0.5 flex-shrink-0">⏰</div>
                    <div>
                      <div className="text-sm font-semibold text-amber-900">Signature pending</div>
                      <p className="text-xs text-amber-700 mt-1">
                        {!allTenantsSigned && !landlordSigned && "Both tenant and owner signatures are required."}
                        {!allTenantsSigned && landlordSigned && `Waiting for tenant signature${ag.tenant_signatures && ag.tenant_signatures.length > 1 ? 's' : ''}.`}
                        {allTenantsSigned && !landlordSigned && "Waiting for owner signature."}
                        {ag.signature_reminder_sent_at && " A reminder was sent 48 hours ago."}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* PDF Preview Card - Shows same beautiful PDF as step 8 with signature status */}
              {ag.id && (
                <AgreementPreviewCard
                  agreement={ag}
                  rental={r}
                  onDownload={() => toast.success("✅ PDF downloaded!")}
                />
              )}

              {r.agreement_type === "traditional_paper" ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm space-y-2">
                  <div className="font-medium text-amber-800">📄 Traditional Paper Agreement</div>
                  <p className="text-amber-700">This is a traditional paper-based agreement. Orello is not managing deposit protection or checkout for this rental. All arrangements are between you and your landlord directly.</p>
                </div>
              ) : r.agreement_type === "external" ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm space-y-2">
                  <div className="font-medium text-amber-800">📄 Externally Signed Agreement</div>
                  <p className="text-amber-700">This agreement was signed directly between the parties outside Orello. No terms are stored on this platform. Orello is only managing the deposit and checkout for this rental.</p>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-xs text-blue-700">
                    ℹ️ This is an <strong>Orello E-Agreement</strong> — a digital service agreement facilitated by Orello. It is not a state-issued legal document.
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                    <div><span className="text-gray-500">Notice period:</span> {ag.notice_period_days} days</div>
                    <div><span className="text-gray-500">Maintenance:</span> {ag.maintenance_responsibilities}</div>
                    <div><span className="text-gray-500">Disputes:</span> {ag.dispute_clause}</div>
                    <div><span className="text-gray-500">Early exit:</span> {ag.early_exit_clause}</div>
                  </div>
                </>
              )}
              <div className="card-base p-4">
                <div className="text-sm font-medium mb-2">Signatures</div>
                {/* PHASE 5: Display tenant signatures from array */}
                {ag.tenant_signatures && ag.tenant_signatures.map((sig, i) => {
                  const tenant = r.tenants_resolved?.find(t => t.id === sig.tenant_id);
                  const label = i === 0 ? `Lead Tenant · ${tenant?.user?.name || "—"}` : `Tenant ${i + 1} · ${tenant?.user?.name || "—"}`;
                  const showResend = isCreator && ag.status === 'pending_signatures' && !sig.signed_at;
                  return (
                    <SignatureRow
                      key={i}
                      label={label}
                      signed={!!sig.signed_at}
                      at={sig.signed_at}
                      onResend={showResend ? handleResendInvite : null}
                      isResending={resendingId === ag.id}
                    />
                  );
                })}
                <SignatureRow
                  label={`Owner · ${r.landlord?.name}`}
                  signed={ag.signed_by_landlord}
                  at={ag.landlord_signed_at}
                  onResend={isCreator && ag.status === 'pending_signatures' && !ag.signed_by_landlord ? handleResendInvite : null}
                  isResending={resendingId === ag.id}
                />
                {/* Witnesses are optional at creation, but once a witness is designated
                    (name provided), their signature is a required, tracked item here.
                    It does NOT block the agreement lock or pipeline - witnesses matter
                    for the offline print/stamp path, not digital signing (see terms). */}
                {ag.additional?.witness1_name && (
                  <SignatureRow
                    label={`Witness 1 · ${ag.additional.witness1_name}`}
                    signed={!!ag.witness1_signed_at}
                    at={ag.witness1_signed_at}
                  />
                )}
                {ag.additional?.witness2_name && (
                  <SignatureRow
                    label={`Witness 2 · ${ag.additional.witness2_name}`}
                    signed={!!ag.witness2_signed_at}
                    at={ag.witness2_signed_at}
                  />
                )}
              </div>
              {!ag.locked && (() => {
                // PRODUCTION RULE: Only tenants and landlords can sign
                // Brokers, Agents, and Self-managed landlords are creators and should NOT sign
                const isCreator = ["broker_admin", "agent"].includes(user?.role) ||
                                (user?.role === "landlord" && user?.self_managed);

                let userHasNotSigned = false;
                if (user.role === "tenant") {
                  // PHASE 5: Check if this tenant has signed in the tenant_signatures array
                  const tenantSig = ag.tenant_signatures?.find(sig => sig.tenant_id === user.id);
                  userHasNotSigned = !tenantSig?.signed_at;
                } else if (user.role === "landlord" && !isCreator) {
                  userHasNotSigned = !ag.signed_by_landlord;
                }

                return !isCreator && userHasNotSigned;
              })() && (
                <div className="card-base p-5 bg-[#FFF4ED]/40 border-orange-100 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                      <PenLine className="h-4 w-4 text-[#F97316]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Your signature is required</div>
                      <p className="text-xs text-gray-500 mt-0.5">Draw or type your signature. You'll review the legal declaration before confirming.</p>
                    </div>
                  </div>
                  <button
                    data-testid="sign-btn"
                    onClick={() => setShowSigningWizard(true)}
                    disabled={user?.kyc_status !== "verified"}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Sign Agreement
                  </button>
                  {user?.kyc_status !== "verified" && (
                    <p className="text-xs text-amber-700 text-center">
                      ⚠️ Complete KYC to sign this agreement.{" "}
                      <a href="/onboarding/kyc" className="underline font-medium">Start KYC →</a>
                    </p>
                  )}
                </div>
              )}
              {!ag.locked && ["agent", "broker_admin"].includes(user.role) && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                  You will be notified once all parties have signed. No action needed from you at this stage.
                </div>
              )}
              {ag.locked && <div className="text-sm text-emerald-700 bg-emerald-50 p-4 rounded-lg flex items-center gap-2"><Lock className="h-4 w-4" /> Agreement signed by all parties.</div>}
            </div>
          )}

          {tab === "deposit" && (
            <div className="space-y-4" data-testid="pane-deposit">
              <h3 className="text-lg font-semibold font-display">Deposit</h3>
              {!r.deposit_protection ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-2">
                  <div className="flex items-center gap-2 text-gray-700 font-medium">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Deposit not held by Orello
                  </div>
                  <p className="text-sm text-gray-600">
                    The deposit of {formatINR(r.deposit_amount)} is held directly by the landlord for this tenancy.
                    Orello only managed the agreement signing. Condition reports and settlement are not available for this rental.
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    To get full deposit protection on future rentals, ask your agent to enable it when creating the rental.
                  </p>
                </div>
              ) : r.tenants_resolved?.[0]?.payment_status === "paid" ? (
                <div className="card-base p-6 bg-emerald-50 border border-emerald-200 space-y-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-6 w-6 text-emerald-600" />
                    <div className="text-lg font-semibold text-emerald-700">✓ Deposit Protected</div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Deposit Amount</span>
                      <span className="font-semibold text-gray-900">{formatINR(r.deposit_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Platform Fee Paid</span>
                      <span className="font-semibold text-gray-900">{formatINR((() => {
                        const TIER_1_THRESHOLD = 15000000;
                        const TIER_2_THRESHOLD = 150000000;
                        const TIER_1_RATE = 0.005;
                        const TIER_2_RATE = 0.007;
                        const TIER_3_RATE = 0.01;
                        const PLATFORM_FEE_MIN = 25000;
                        if (r.deposit_amount <= TIER_1_THRESHOLD) {
                          return Math.max(Math.floor(r.deposit_amount * TIER_1_RATE), PLATFORM_FEE_MIN);
                        } else if (r.deposit_amount <= TIER_2_THRESHOLD) {
                          return Math.floor(r.deposit_amount * TIER_2_RATE);
                        } else {
                          return Math.floor(r.deposit_amount * TIER_3_RATE);
                        }
                      })())}</span>
                    </div>
                    {r.tenancy_end && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">Protected Until</span>
                        <span className="font-semibold text-gray-900">{formatDate(r.tenancy_end)}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">Your deposit is held securely and released only when both you and your landlord agree. No deductions can be made without evidence.</p>
                </div>
              ) : (
                <>
                  {/* Deposit payment — sequential multi-tenant flow */}
                  {(() => {
                    const TIER_1_THRESHOLD = 15000000;
                    const TIER_2_THRESHOLD = 150000000;
                    const PLATFORM_FEE_MIN = 25000;

                    const tenants = r.tenants || [];
                    const numTenants = tenants.length;
                    const depositAmount = r.deposit_amount;

                    // Total Orello fee on full deposit, split equally
                    let totalFee;
                    if (depositAmount <= TIER_1_THRESHOLD) totalFee = Math.max(Math.floor(depositAmount * 0.005), PLATFORM_FEE_MIN);
                    else if (depositAmount <= TIER_2_THRESHOLD) totalFee = Math.floor(depositAmount * 0.007);
                    else totalFee = Math.floor(depositAmount * 0.01);
                    const feePerTenant = Math.round(totalFee / numTenants);

                    // This tenant's position and paid status
                    const myIndex = tenants.findIndex(t => t.tenant_id === user.id);
                    const myTenant = myIndex >= 0 ? tenants[myIndex] : null;
                    const currentTenantPaid = myTenant?.payment_status === "paid";

                    // Paid so far by earlier tenants
                    const paidSoFar = tenants.reduce((s, t) => s + (t.payment_status === "paid" ? (t.share_amount || 0) : 0), 0);
                    const remaining = depositAmount - paidSoFar;

                    // Who's next to pay (sequential)
                    const unpaid = tenants.filter(t => t.payment_status !== "paid");
                    const isMyTurn = unpaid.length > 0 && unpaid[0].tenant_id === user.id;
                    const isLastTenant = isMyTurn && unpaid.length === 1;

                    // Input validation
                    const inputPaise = Math.round(Number(depositShareInput || 0) * 100);
                    const inputValid = isLastTenant || (inputPaise > 0 && inputPaise < remaining);

                    const myShare = isLastTenant ? remaining : inputPaise;
                    const feePayer = r.fee_payer || "tenant";
                    const myFeeComponent = feePayer === "landlord" ? 0 : feePayer === "split" ? Math.ceil(feePerTenant / 2) : feePerTenant;
                    const myTotal = myShare + myFeeComponent;

                    return (
                      <div className="space-y-4">
                        {/* Progress: paid so far */}
                        {numTenants > 1 && (
                          <div className="card-base p-4 space-y-2">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Deposit progress</div>
                            {tenants.map((t, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-gray-600">Tenant {i + 1}{t.tenant_id === user.id ? " (you)" : ""}</span>
                                <span className={t.payment_status === "paid" ? "text-emerald-600 font-medium" : "text-gray-400"}>
                                  {t.payment_status === "paid" ? `✓ ${formatINR(t.share_amount)}` : i === tenants.indexOf(unpaid[0]) ? "your turn" : "waiting"}
                                </span>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm border-t border-gray-100 pt-2">
                              <span className="font-semibold text-gray-700">Remaining</span>
                              <span className="font-semibold text-amber-600">{formatINR(remaining)}</span>
                            </div>
                          </div>
                        )}

                        {/* Share input — only for my turn */}
                        {user.role === "tenant" && r.status === "payment_pending" && !currentTenantPaid && isMyTurn && (
                          <div className="card-base p-5 space-y-4">
                            {isLastTenant ? (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-1">Your deposit share</div>
                                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-gray-500 text-sm">₹</span>
                                  <span className="font-semibold text-gray-900">{(remaining / 100).toLocaleString("en-IN")}</span>
                                  <span className="text-xs text-gray-400 ml-auto">(remaining balance — auto-assigned)</span>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <label className="text-sm font-medium text-gray-700">How much will you pay? (₹)</label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {paidSoFar > 0
                                    ? `₹${(paidSoFar/100).toLocaleString("en-IN")} already paid. Balance: ₹${(remaining/100).toLocaleString("en-IN")} — enter your portion.`
                                    : `Total deposit: ₹${(depositAmount/100).toLocaleString("en-IN")} — enter how much you'll pay.`}
                                </p>
                                <div className="mt-2 flex items-stretch border border-gray-300 rounded-lg overflow-hidden">
                                  <span className="px-3 flex items-center bg-gray-50 text-sm text-gray-500 border-r">₹</span>
                                  <input
                                    type="number"
                                    value={depositShareInput}
                                    onChange={e => setDepositShareInput(e.target.value)}
                                    placeholder={`e.g. ${Math.round(remaining / 200) * 100 / 100}`}
                                    min={1}
                                    max={(remaining / 100) - 1}
                                    className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                                  />
                                </div>
                                {inputPaise >= remaining && (
                                  <p className="text-xs text-red-500 mt-1">Must be less than ₹{(remaining/100).toLocaleString("en-IN")} — leave some for the other tenant{unpaid.length > 1 ? "s" : ""}.</p>
                                )}
                              </div>
                            )}

                            <div className="space-y-2 border-t border-gray-100 pt-3">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Your deposit share</span>
                                <span className="font-medium">{myShare > 0 ? formatINR(myShare) : "—"}</span>
                              </div>
                              <div className="flex justify-between font-semibold border-t border-gray-100 pt-2">
                                <span>Amount to pay</span>
                                <span className="text-[#F97316] text-lg">{myShare > 0 ? formatINR(myShare) : "—"}</span>
                              </div>
                            </div>

                            <button
                              data-testid="pay-deposit"
                              onClick={() => payDeposit(isLastTenant ? null : inputPaise)}
                              disabled={busy || !inputValid}
                              className="w-full bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50"
                            >
                              {busy ? "Processing…" : myShare > 0 ? `Pay ${formatINR(myShare)}` : "Enter your share to continue"}
                            </button>
                          </div>
                        )}

                        {/* Not your turn yet */}
                        {user.role === "tenant" && r.status === "payment_pending" && !currentTenantPaid && !isMyTurn && (
                          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 flex items-start gap-3">
                            <div className="text-amber-500 mt-0.5 flex-shrink-0">⏳</div>
                            <div className="text-sm text-amber-700">
                              <div className="font-medium">Waiting for the previous tenant to pay first</div>
                              <div className="text-xs mt-1">You'll be notified when it's your turn.</div>
                            </div>
                          </div>
                        )}

                        {/* Already paid */}
                        {user.role === "tenant" && r.status === "payment_pending" && currentTenantPaid && (
                          <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 flex items-start gap-3">
                            <div className="text-green-600 text-lg flex-shrink-0">✓</div>
                            <div className="text-sm text-green-700">
                              <div className="font-medium">Your payment is complete — {formatINR(myTenant?.share_amount || 0)} secured</div>
                              {unpaid.length > 0 && <div className="text-xs mt-1">Waiting for {unpaid.length} other tenant{unpaid.length > 1 ? "s" : ""} to pay.</div>}
                            </div>
                          </div>
                        )}

                        {/* Info */}
                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 flex items-start gap-3">
                          <div className="text-blue-500 mt-0.5 flex-shrink-0">ⓘ</div>
                          <p className="text-xs text-blue-700">The deposit protection fee was already paid during agreement signing. Your deposit will be held in escrow and refunded in full minus any agreed deductions.</p>
                        </div>

                        {user.role === "tenant" && r.status === "agreement_pending" && (
                          <p className="text-sm text-amber-600">Sign the agreement first.</p>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Proof-service framing: Orello holds no deposit here */}
          {r.proof_service && !r.deposit_protection && ["movein", "ofix", "moveout", "settlement"].includes(tab) && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-sm font-semibold text-amber-800">📄 Documentation &amp; proof service</div>
              <p className="text-xs text-amber-700 mt-1">
                Orello does <strong>not</strong> hold the deposit for this rental — the landlord controls the money.
                These check-in / checkout condition reports (and any O-Fix history) are saved as documented, court-usable proof.
                There is no Orello deduction, claim, or payout here; any settlement is between you and the landlord directly.
              </p>
            </div>
          )}

          {tab === "movein" && (
            <MoveinPane rental={r} user={user} isCreator={isCreator} rooms={rooms} onDone={load} />
          )}

          {tab === "ofix" && (
            <OFix rentalId={r.id} embedded />
          )}

          {tab === "moveout" && (
            <MoveoutPane rental={r} user={user} isCreator={isCreator} rooms={rooms} deductionReasons={deductionReasons} onDone={load} />
          )}

          {tab === "settlement" && (
            <SettlementPane r={r} onDone={load} />
          )}

          {tab === "ratings" && (
            <RatingsPane r={r} onDone={load} />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal - at top level so it's always available */}
      {showDeleteConfirm ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-6 w-6" />
              <h3 className="font-semibold text-lg">Delete Agreement?</h3>
            </div>
            <p className="text-sm text-gray-600">
              This will permanently delete the agreement for <strong>{r.property_address}</strong> and all associated data. This action cannot be undone.
            </p>
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-xs text-red-700">
                ⚠️ Both tenant and landlord will no longer see this agreement on their dashboards.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAgreement}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Move-in Pane ────────────────────────────────────────────────────────────

function MoveinPane({ rental, user, isCreator, rooms, onDone }) {
  const [report, setReport] = useState(null);
  const [reportNotFound, setReportNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roomData, setRoomData] = useState({});
  const [overallNotes, setOverallNotes] = useState("");
  const [checkinPhotos, setCheckinPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState({});
  const [counterPhotos, setCounterPhotos] = useState({}); // {room: [photos]} — proof for a tenant counter
  const [keyHandoverConfirmed, setKeyHandoverConfirmed] = useState(false);

  // Re-fetchable so the pane updates after submit / sign (the report persists on
  // the server until end of tenancy; only the local view needed refreshing).
  const loadReport = useCallback(async () => {
    try {
      // Backend returns null with HTTP 200 (not 404) when no report exists yet.
      const { data } = await api.get(`/rentals/${rental.id}/checkin-report`);
      if (data) { setReport(data); setReportNotFound(false); }
      else { setReport(null); setReportNotFound(true); }
    } catch (e) {
      if (e.response?.status === 404) { setReport(null); setReportNotFound(true); }
    } finally {
      setLoading(false);
    }
  }, [rental.id]);

  useEffect(() => { loadReport(); }, [loadReport]);

  useEffect(() => {
    if (rooms.length) {
      const init = {};
      rooms.forEach(r => { init[r] = { condition: "good", notes: "" }; });
      setRoomData(init);
    }
  }, [rooms]);

  const submitReport = async () => {
    // ── PHASE 4: Require key handover confirmation ──
    if (!keyHandoverConfirmed) {
      return toast.error("Please confirm key handover before submitting the report.");
    }
    const incomplete = rooms.filter(r => {
      const rd = roomData[r];
      if (rd?.not_applicable) return false; // excluded sections need nothing
      return !rd?.condition || !rd?.notes?.trim() || !(rd?.photos?.length > 0);
    });
    if (incomplete.length > 0) {
      return toast.error(`Please complete all rooms before submitting: ${incomplete.slice(0,3).join(", ")}${incomplete.length > 3 ? ` + ${incomplete.length - 3} more` : ""}`);
    }
    const roomList = rooms.map(r => {
      const rd = roomData[r] || {};
      return {
        room: r,
        not_applicable: !!rd.not_applicable,
        condition: rd.not_applicable ? "" : (rd.condition || "good"),
        notes: rd.not_applicable ? "" : (rd.notes || ""),
        photo_urls: rd.not_applicable ? [] : (rd.photos || []).map(p => p.url),
        add_to_ofix: !!(rd.add_to_ofix && !rd.not_applicable),
      };
    });
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/movein-report`, {
        key_handover_confirmed: keyHandoverConfirmed,
        rooms: roomList,
        overall_notes: overallNotes,
        photo_urls: []
      });
      toast.success("Start of Tenancy report submitted.");
      await loadReport();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Tenant sign. Countering is optional — but any room the tenant comments on
  // must carry a proof photo. Blank rooms need nothing; zero counters is fine.
  const acknowledge = async () => {
    const counters = [];
    for (const rm of (report?.rooms || [])) {
      const comment = (remarks[rm.room] || "").trim();
      const photos = (counterPhotos[rm.room] || []).map(p => p.url);
      if (comment && photos.length === 0) {
        return toast.error(`Attach a photo to your comment on ${rm.room}, or clear the comment.`);
      }
      if (comment) counters.push({ room: rm.room, comment, photo_urls: photos });
    }
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/movein-report/acknowledge`, { remarks: counters });
      toast.success(counters.length ? "Signed with your counter(s). Sent to the landlord." : "Signed. Sent to the landlord.");
      await loadReport();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Landlord acknowledges & signs after the tenant — this locks the report.
  const landlordSign = async () => {
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/movein-report/landlord-sign`);
      toast.success("Acknowledged & signed. The report is now locked.");
      await loadReport();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;

  // ── CASE 1: Report exists and is locked — show read-only view for EVERYONE ──
  if (report?.locked) {
    return (
      <div className="space-y-4" data-testid="pane-movein">
        <div className="text-sm text-emerald-700 bg-emerald-50 p-4 rounded-lg flex items-center gap-2">
          <Lock className="h-4 w-4" /> Start of Tenancy report locked on {formatDate(report.submitted_at)}
        </div>
        <div className="card-base divide-y divide-gray-100 px-2 py-1">
          {report.rooms?.map((rm, i) => (
            <CheckoutRoomRow key={i} rm={rm} />
          ))}
        </div>
        {report.overall_notes && <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{report.overall_notes}</div>}
        <div className="flex flex-wrap gap-2">
          {(report.tenant_signed || report.tenant_acknowledged) && (
            <span className="pill text-xs bg-emerald-50 text-emerald-700">Tenant signed · {formatDate(report.tenant_signed_at || report.tenant_acknowledged_at)}</span>
          )}
          {report.landlord_signed && (
            <span className="pill text-xs bg-emerald-50 text-emerald-700">Landlord signed · {formatDate(report.landlord_signed_at)}</span>
          )}
        </div>
        {report.tenant_remarks?.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Tenant counters</div>
            {report.tenant_remarks.map((rem, i) => (
              <div key={i} className="card-base p-3 space-y-2">
                <div className="text-xs text-gray-500">{rem.room}</div>
                <div className="text-sm">{rem.comment}</div>
                <MediaGrid urls={rem.photo_urls} room={rem.room} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isLandlord = user.role === "landlord" && rental.landlord_id === user.id;

  // Read-only render of the submitted report (shared by several states).
  const reportRooms = (
    <div className="card-base divide-y divide-gray-100 px-2 py-1">
      {report?.rooms?.map((rm, i) => (
        <CheckoutRoomRow key={i} rm={rm} />
      ))}
    </div>
  );
  const tenantCountersView = report?.tenant_remarks?.length > 0 ? (
    <div className="space-y-2">
      <div className="text-sm font-medium">Tenant counters</div>
      {report.tenant_remarks.map((rem, i) => (
        <div key={i} className="card-base p-3 space-y-2">
          <div className="text-xs text-gray-500">{rem.room}</div>
          <div className="text-sm">{rem.comment}</div>
          <MediaGrid urls={rem.photo_urls} room={rem.room} />
        </div>
      ))}
    </div>
  ) : null;

  // ── CASE 2: Report submitted, not locked — TENANT reviews & signs first ──
  if (report && !report.locked && user.role === "tenant") {
    if (report.tenant_signed) {
      return (
        <div className="space-y-4" data-testid="pane-movein">
          <div className="text-sm text-indigo-900 bg-[#FFF4ED] p-4 rounded-lg">
            ✓ You signed on {formatDate(report.tenant_signed_at)}. Waiting for the landlord to acknowledge & sign.
          </div>
          {reportRooms}
        </div>
      );
    }
    return (
      <div className="space-y-4" data-testid="pane-movein">
        <div>
          <h3 className="text-lg font-semibold font-display">Review & Sign Start of Tenancy Report</h3>
          <p className="text-sm text-gray-500 mt-1">
            If everything looks right, just sign & proceed. Only counter a room if you think it's wrong or unfair —
            a counter needs a proof photo.
          </p>
        </div>
        <div className="space-y-2">
          {report.rooms?.map((rm, i) => {
            const comment = remarks[rm.room] || "";
            const cPhotos = counterPhotos[rm.room] || [];
            return (
              <div key={i} className="card-base p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{rm.room}</div>
                  {rm.not_applicable
                    ? <span className="pill text-xs bg-gray-100 text-gray-500">Not applicable</span>
                    : <span className={`pill text-xs ${condPill(rm.condition)}`}>{rm.condition}</span>}
                </div>
                {!rm.not_applicable && (<>
                  {rm.notes && <div className="text-xs text-gray-500">{rm.notes}</div>}
                  <MediaGrid urls={rm.photo_urls} room={rm.room} />
                  <input
                    value={comment}
                    onChange={(e) => setRemarks({ ...remarks, [rm.room]: e.target.value })}
                    placeholder="Counter this room? Add a comment (optional)"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />
                  {comment.trim() && (
                    <div>
                      <PhotoUpload value={cPhotos} onChange={(p) => setCounterPhotos({ ...counterPhotos, [rm.room]: p })} label="Add proof photo — required for a counter" />
                      {cPhotos.length === 0 && (
                        <div className="text-xs text-amber-600 mt-1">A photo is required to counter this room.</div>
                      )}
                    </div>
                  )}
                </>)}
              </div>
            );
          })}
        </div>
        <button onClick={acknowledge} disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-5 py-2.5 rounded-lg font-medium text-sm">
          {busy ? "Submitting…" : "Sign & proceed"}
        </button>
      </div>
    );
  }

  // ── CASE 3a: LANDLORD acknowledges & signs after the tenant (locks the report) ──
  // Checked before the creator branch so a self-managed landlord (also the creator)
  // reaches the sign action rather than the waiting view.
  if (report && !report.locked && isLandlord) {
    if (!report.tenant_signed) {
      return (
        <div className="space-y-4" data-testid="pane-movein">
          <div className="text-sm text-amber-700 bg-amber-50 p-4 rounded-lg">
            ⏰ Report submitted. Waiting for the tenant to review & sign first.
          </div>
          {reportRooms}
        </div>
      );
    }
    return (
      <div className="space-y-4" data-testid="pane-movein">
        <div>
          <h3 className="text-lg font-semibold font-display">Acknowledge & Sign</h3>
          <p className="text-sm text-gray-500 mt-1">The tenant has reviewed and signed. Acknowledge & sign to finalise and lock the report.</p>
        </div>
        {reportRooms}
        {tenantCountersView}
        <button onClick={landlordSign} disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-5 py-2.5 rounded-lg font-medium text-sm">
          {busy ? "Signing…" : "Acknowledge & Sign"}
        </button>
      </div>
    );
  }

  // ── CASE 3b: CREATOR (broker/agent) waiting for tenant → landlord to sign ──
  if (report && !report.locked && isCreator) {
    return (
      <div className="space-y-4" data-testid="pane-movein">
        <div className="text-sm text-amber-700 bg-amber-50 p-4 rounded-lg">
          ⏰ Report submitted. {report.tenant_signed ? "Tenant signed — waiting for the landlord to acknowledge & sign." : "Waiting for the tenant to review & sign, then the landlord."}
        </div>
        {reportRooms}
        {tenantCountersView}
      </div>
    );
  }

  // ── CASE 4: No report yet — creator can submit (only if rental is active) ──
  if (reportNotFound && isCreator) {
    if (!["active", "expired_continuing"].includes(rental.status)) {
      return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">Complete agreement and deposit payment to unlock this step.</div>;
    }
    // 3-day submission window from deposit-paid (activation). After it closes, no
    // report can be filed — at end of tenancy this is treated in the tenant's favour.
    const activatedAt = rental.activated_at ? new Date(rental.activated_at) : null;
    const windowClosed = activatedAt && (Date.now() - activatedAt.getTime()) > 3 * 24 * 60 * 60 * 1000;
    if (windowClosed) {
      return <div className="text-sm text-amber-700 bg-amber-50 p-4 rounded-lg">The 3-day window to submit the Start of Tenancy report has closed. No report was filed — at end of tenancy this is treated in the tenant's favour.</div>;
    }
    return (
      <div className="space-y-4" data-testid="pane-movein">
        <div>
          <h3 className="text-lg font-semibold font-display">Start of Tenancy Report</h3>
          <p className="text-sm text-gray-500 mt-1">Document the property condition room by room. This protects everyone at end of tenancy.</p>
          {rental.furnishing_type && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mt-2">
              This property is <span className="font-medium">{({ unfurnished: "Unfurnished", part_furnished: "Part-furnished", fully_furnished: "Fully furnished" }[rental.furnishing_type]) || rental.furnishing_type}</span>. Tick <span className="font-medium">"Not in this property"</span> on any section that doesn't exist here — no photos needed for those.
            </div>
          )}
        </div>

        {/* ── PHASE 4: Key Handover Confirmation (Mandatory First Item) ── */}
        <div className="card-base p-5 border-l-4 border-emerald-500 bg-emerald-50/30 space-y-3">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={keyHandoverConfirmed}
              onChange={(e) => setKeyHandoverConfirmed(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 cursor-pointer"
              data-testid="key-handover-confirm"
            />
            <div className="flex-1">
              <label className="text-sm font-semibold text-gray-900 cursor-pointer">Key handover confirmed</label>
              <p className="text-xs text-gray-600 mt-1">I confirm that the tenant has received the keys and can access the property.</p>
              {!keyHandoverConfirmed && (
                <p className="text-xs text-emerald-700 font-medium mt-2">✓ This is required to proceed</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {rooms.map(room => (
            <RoomCard key={room} room={room} value={roomData[room]} onChange={(v) => setRoomData({ ...roomData, [room]: v })} />
          ))}
        </div>
        <textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)}
          placeholder="Overall property notes (optional)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={3} />

        {/* ── PHASE 4: 3-Day Submission Deadline Info ── */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <div className="font-medium text-amber-900">⏰ 3-day deadline</div>
          <p className="text-xs text-amber-700 mt-1">Submit this report within 3 days of move-in. Tenant will have 72 hours to acknowledge.</p>
        </div>

        <button onClick={submitReport} disabled={busy || !keyHandoverConfirmed} className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-5 py-2.5 rounded-lg font-medium text-sm disabled:opacity-50">
          {busy ? "Submitting…" : "Submit Start of Tenancy Report"}
        </button>
      </div>
    );
  }

  // ── CASE 5: No report, tenant viewing — waiting for agent/landlord to submit ──
  if (reportNotFound && user.role === "tenant") {
    if (["active", "expired_continuing"].includes(rental.status)) {
      return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">Start of Tenancy report not submitted yet. Your agent or landlord will document the property condition.</div>;
    }
    return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">Start of Tenancy step pending.</div>;
  }

  return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">Loading report…</div>;
}

// ─── Read-only room row (locked checkin view + checkout review) ──────────────
// MediaGrid imported from components/MediaLightbox — lightbox with keyboard nav

// CheckoutRoomRow — matches admin RoomRow style, with optional side-by-side comparison
function CheckoutRoomRow({ rm, checkinRoom, tenantRemarks }) {
  const [open, setOpen] = useState(false);
  const hasCheckin  = !!checkinRoom;
  const condChanged = hasCheckin && checkinRoom.condition !== rm.condition;

  return (
    <div className={`border-b border-gray-100 last:border-0 ${condChanged ? "bg-amber-50/30" : ""}`}>
      {/* Row header */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 py-2.5 px-1 rounded-lg hover:bg-black/5 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${open ? "" : "-rotate-90"}`} />
          <span className="text-sm font-medium text-gray-800 truncate">{rm.room}</span>
          {condChanged && <span className="text-amber-500 text-xs font-bold">↕</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {rm.not_applicable ? (
            <span className="pill text-xs bg-gray-100 text-gray-500">Not applicable</span>
          ) : (
            <>
              {hasCheckin && (
                <span className={`pill text-xs ${condPill(checkinRoom.condition)}`}>
                  In: {checkinRoom.condition}
                </span>
              )}
              <span className={`pill text-xs ${condPill(rm.condition)}`}>
                {hasCheckin ? `Out: ${rm.condition}` : rm.condition}
              </span>
            </>
          )}
        </div>
      </button>

      {/* Expanded panel */}
      {open && (
        <div className="pb-3 px-4">
          {hasCheckin ? (
            /* Side-by-side move-in vs move-out */
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Move-in</div>
                {checkinRoom.notes && <p className="text-xs text-gray-500 italic">{checkinRoom.notes}</p>}
                {tenantRemarks && (
                  <div className="bg-blue-50 rounded-lg px-2.5 py-2">
                    <div className="text-xs font-medium text-blue-600 mb-0.5">Tenant noted</div>
                    <div className="text-xs text-blue-700">{tenantRemarks}</div>
                  </div>
                )}
                <MediaGrid urls={checkinRoom.photo_urls} room={`${rm.room} move-in`} />
              </div>
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Move-out</div>
                {rm.notes && <p className="text-xs text-gray-500 italic">{rm.notes}</p>}
                <MediaGrid urls={rm.photo_urls} room={`${rm.room} move-out`} />
              </div>
            </div>
          ) : (
            /* Single column */
            <div className="space-y-2">
              {rm.notes && <p className="text-xs text-gray-500 italic">{rm.notes}</p>}
              <MediaGrid urls={rm.photo_urls} room={rm.room} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// CheckoutReviewRow — acknowledger review row: photo comparison + inline
// "deduct for this damage" control (checkbox → amount + comment + photos).
function CheckoutReviewRow({ rm, checkinRoom, tenantRemarks, deduct, onDeductChange }) {
  const [open, setOpen] = useState(false);
  const hasCheckin = !!checkinRoom;
  const condChanged = hasCheckin && checkinRoom.condition !== rm.condition;
  const on = !!deduct;
  const amtPaise = Math.round(Number(deduct?.amount || 0) * 100);
  const set = (patch) => onDeductChange({ ...(deduct || { amount: "", comment: "", photos: [] }), ...patch });

  return (
    <div className={`border-b border-gray-100 last:border-0 ${on ? "bg-red-50/40" : condChanged ? "bg-amber-50/30" : ""}`}>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 py-2.5 px-1 rounded-lg hover:bg-black/5 transition-colors text-left">
        <div className="flex items-center gap-2 min-w-0">
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${open ? "" : "-rotate-90"}`} />
          <span className="text-sm font-medium text-gray-800 truncate">{rm.room}</span>
          {condChanged && <span className="text-amber-500 text-xs font-bold">↕</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {on && amtPaise > 0 && <span className="pill text-xs bg-red-100 text-red-700 border border-red-200">− {formatINR(amtPaise)}</span>}
          {rm.not_applicable ? (
            <span className="pill text-xs bg-gray-100 text-gray-500">Not applicable</span>
          ) : (
            <>
              {hasCheckin && <span className={`pill text-xs ${condPill(checkinRoom.condition)}`}>In: {checkinRoom.condition}</span>}
              <span className={`pill text-xs ${condPill(rm.condition)}`}>{hasCheckin ? `Out: ${rm.condition}` : rm.condition}</span>
            </>
          )}
        </div>
      </button>

      {open && (
        <div className="pb-3 px-4 space-y-3">
          {/* Photo comparison */}
          {hasCheckin ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Move-in</div>
                {checkinRoom.notes && <p className="text-xs text-gray-500 italic">{checkinRoom.notes}</p>}
                {tenantRemarks && (
                  <div className="bg-blue-50 rounded-lg px-2.5 py-2">
                    <div className="text-xs font-medium text-blue-600 mb-0.5">Tenant noted</div>
                    <div className="text-xs text-blue-700">{tenantRemarks}</div>
                  </div>
                )}
                <MediaGrid urls={checkinRoom.photo_urls} room={`${rm.room} move-in`} />
              </div>
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Move-out</div>
                {rm.notes && <p className="text-xs text-gray-500 italic">{rm.notes}</p>}
                <MediaGrid urls={rm.photo_urls} room={`${rm.room} move-out`} />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {rm.notes && <p className="text-xs text-gray-500 italic">{rm.notes}</p>}
              <MediaGrid urls={rm.photo_urls} room={rm.room} />
            </div>
          )}

          {/* Inline deduction — only for applicable rooms */}
          {!rm.not_applicable && (
            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={on}
                  onChange={(e) => onDeductChange(e.target.checked ? { amount: "", comment: "", photos: [] } : null)}
                  className="h-4 w-4 rounded border-gray-300 text-[#F97316]" />
                <span className="text-sm font-medium text-gray-800">Deduct from the deposit for this damage</span>
              </label>
              {on && (
                <div className="space-y-2 pl-6">
                  <input type="number" value={deduct.amount} onChange={(e) => set({ amount: e.target.value })}
                    placeholder="Amount to deduct (₹)"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  <textarea value={deduct.comment || ""} onChange={(e) => set({ comment: e.target.value })}
                    placeholder="Reason / comment — describe the damage (shown to the tenant and used as evidence)"
                    rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Supporting photos <span className="text-gray-400">(recommended)</span></div>
                    <PhotoUpload value={deduct.photos || []} onChange={(p) => set({ photos: p })} label="Add photos of the damage" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── End of Tenancy Pane ────────────────────────────────────────────────────────────

function MoveoutPane({ rental, user, isCreator, rooms, deductionReasons = [], onDone }) {
  const [checkoutData, setCheckoutData] = useState(null);
  const [checkinData, setCheckinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moveoutDate, setMoveoutDate] = useState("");
  const [roomData, setRoomData] = useState({});
  const [overallNotes, setOverallNotes] = useState("");
  const [acknowledger, setAcknowledger] = useState("self");
  const [deductByRoom, setDeductByRoom] = useState({}); // { [room]: { amount, comment, photos } }
  const [tenantResponse, setTenantResponse] = useState({});
  const [tenantPhotos, setTenantPhotos] = useState({});
  const [busy, setBusy] = useState(false);
  const [endChoice, setEndChoice] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get(`/rentals/${rental.id}/checkout-report`).catch(() => null),
      api.get(`/rentals/${rental.id}/checkin-report`).catch(() => null),
    ]).then(([checkout, checkin]) => {
      if (checkout) setCheckoutData(checkout.data);
      if (checkin) setCheckinData(checkin.data);
      setLoading(false);
    });
  }, [rental.id]);

  // Auto-poll while ORE-AI is analysing a freshly raised dispute so the verdict
  // appears on its own — no manual refresh. ORE-AI is instant, so this normally
  // resolves on the first tick; give up after ~1 min to avoid polling forever.
  const disputeStatus = checkoutData?.dispute?.status;
  useEffect(() => {
    if (rental.status !== "dispute_review" && rental.status !== "dispute") return;
    if (disputeStatus && disputeStatus !== "pending") return; // already reviewed
    let attempts = 0;
    let rechecked = false;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const res = await api.get(`/rentals/${rental.id}/checkout-report`);
        if (res?.data) {
          setCheckoutData(res.data);
          if (res.data?.dispute && res.data.dispute.status !== "pending") {
            clearInterval(iv);
            onDone?.(); // refresh parent so rental.status reflects the verdict / escalation
            return;
          }
        }
        // ORE-AI is instant; still 'pending' after ~12s means the review errored.
        // Ask the server to self-heal by escalating to human review.
        if (!rechecked && attempts >= 3) {
          rechecked = true;
          await api.post(`/rentals/${rental.id}/dispute/recheck`).catch(() => {});
        }
      } catch (_) { /* keep trying */ }
      if (attempts >= 15) clearInterval(iv);
    }, 4000);
    return () => clearInterval(iv);
  }, [rental.status, disputeStatus, rental.id]);

  useEffect(() => {
    if (rooms.length) {
      const init = {};
      rooms.forEach(r => { init[r] = { condition: "good", notes: "" }; });
      setRoomData(init);
    }
  }, [rooms]);

  const triggerMoveout = async () => {
    if (!moveoutDate) return toast.error("Select an end of tenancy date.");
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/moveout/trigger`, { moveout_date: moveoutDate });
      toast.success("End of Tenancy triggered.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const earlyTerminate = async () => {
    if (!window.confirm("Request early termination? All parties will be notified. This starts the checkout process now.")) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/early-termination`);
      toast.success("Early termination requested. All parties have been notified.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startRenewal = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/rentals/${rental.id}/renewal-draft`);
      navigate('/app/agreement-builder', {
        state: { resumeDraftId: data.draft_id, renewalOfRentalId: rental.id },
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitCheckout = async () => {
    // A section excluded at check-in ("Not in this property") can't exist at
    // checkout either — auto-exclude it, and don't demand condition/notes/photos.
    const isNa = (r) => !!checkinRoom(r)?.not_applicable || !!roomData[r]?.not_applicable;
    const incomplete = rooms.filter(r => !isNa(r) && (!roomData[r]?.condition || !roomData[r]?.notes?.trim() || !(roomData[r]?.photos?.length > 0)));
    if (incomplete.length > 0) {
      return toast.error(`Please complete all rooms before submitting: ${incomplete.slice(0,3).join(", ")}${incomplete.length > 3 ? ` + ${incomplete.length - 3} more` : ""}`);
    }
    const roomList = rooms.map(r => isNa(r) ? ({
      room: r, condition: "", notes: "", photo_urls: [], not_applicable: true,
    }) : ({
      room: r,
      condition: roomData[r]?.condition || "good",
      notes: roomData[r]?.notes || "",
      photo_urls: (roomData[r]?.photos || []).map(p => p.url),
    }));
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/checkout-report`, {
        rooms: roomList, overall_notes: overallNotes, photo_urls: [], acknowledger,
      });
      toast.success("Checkout report submitted.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const proposeDeductions = async () => {
    const items = Object.entries(deductByRoom)
      .filter(([_, d]) => d && Number(d.amount) > 0)
      .map(([room, d]) => ({
        room,
        reason: (d.comment || "").trim() || "Deduction",
        amount: Math.round(Number(d.amount) * 100),
        photo_urls: (d.photos || []).map(p => p.url),
      }));
    if (!items.length) return toast.error("Tick a room and enter an amount to deduct.");
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/checkout-report/propose-deductions`, { items, notes: overallNotes });
      toast.success("Deduction proposal sent to tenant.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmNoDeductions = async () => {
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/checkout-report/no-deductions`);
      toast.success("No deductions confirmed. Full deposit will be refunded.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitTenantResponse = async () => {
    const items = deduction?.items || [];
    // Every item needs an explicit Agree / Disagree decision.
    const undecided = items.filter(it => !tenantResponse[it.room]?.decision);
    if (undecided.length) {
      return toast.error(`Choose Agree or Disagree for every item: ${undecided.slice(0,3).map(i => i.room).join(", ")}${undecided.length > 3 ? " …" : ""}`);
    }
    // Disagreed items must carry a reason (evidence photos strongly recommended).
    const missingReason = items.filter(it => tenantResponse[it.room]?.decision === "disagree" && !tenantResponse[it.room]?.comment?.trim());
    if (missingReason.length) {
      return toast.error(`Add a reason for the items you disagree with: ${missingReason.slice(0,3).map(i => i.room).join(", ")}${missingReason.length > 3 ? " …" : ""}`);
    }
    setBusy(true);
    try {
      // Every item carries a tenant-offered amount: full claimed if agreed, the
      // counter (clamped 0..claimed) if disagreed. tenant_total = sum of these.
      const agreedItems = [];
      const tenantAmounts = {};
      items.forEach(it => {
        const resp = tenantResponse[it.room] || {};
        agreedItems.push(it.room);
        tenantAmounts[it.room] = resp.decision === "agree"
          ? it.amount
          : Math.max(0, Math.min(it.amount, Math.round(Number(resp.amount || 0) * 100)));
      });
      const allPhotoUrls = Object.values(tenantPhotos).flat().map(p => p.url);
      const comments = items
        .filter(it => tenantResponse[it.room]?.comment?.trim())
        .map(it => ({ room: it.room, comment: tenantResponse[it.room].comment }));
      await api.post(`/rentals/${rental.id}/deductions/tenant-response`, {
        agreed_items: agreedItems, tenant_amounts: tenantAmounts,
        notes: comments.map(c => `${c.room}: ${c.comment}`).join(" | "),
        photo_urls: allPhotoUrls,
      });
      toast.success("Response submitted.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const goExternal = async () => {
    if (!window.confirm("Resolve this externally (court / arbitration)? Orello will release your undisputed amount now and hold the disputed amount until an external decision is provided.")) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/deductions/go-external`);
      toast.success("Marked for external resolution. Undisputed amount released to you.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ── Post-verdict dispute responses (ORE-AI verdict / manual decision) ──
  const acceptOrelloDecision = async () => {
    setBusy(true);
    try { await api.post(`/rentals/${rental.id}/dispute/accept`); toast.success("Decision accepted. Settlement finalised."); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [showExternalTerms, setShowExternalTerms] = useState(false);
  const [extTermsAgreed, setExtTermsAgreed] = useState(false);
  const [showClaim, setShowClaim] = useState(false); // inline undisputed-claim form during a dispute
  const takeExternal = () => { setExtTermsAgreed(false); setShowExternalTerms(true); };   // opens the T&C modal
  const confirmTakeExternal = async () => {
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/dispute/external`);
      toast.success("Marked for external resolution. The disputed amount stays held by Orello.");
      setShowExternalTerms(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // External settlement proof (submitted once resolved in court/arbitration)
  const [proofFiles, setProofFiles] = useState([]);
  const [proofNote, setProofNote] = useState("");
  const submitExternalProof = async () => {
    if (!proofFiles.length) return toast.error("Attach at least one proof document (court order / signed settlement).");
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/dispute/external-proof`, {
        proof_urls: proofFiles.map(f => f.url),
        note: proofNote,
      });
      toast.success("Proof submitted. Orello will validate and disburse within 5–7 working days.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestManualReview = async () => {
    if (!window.confirm("Request a free Orello human review? Our team gives a final, binding decision within 7 working days.")) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/dispute/manual-review`);
      toast.success("Human review requested. Orello's team will decide within 7 working days.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [rejectStep, setRejectStep] = useState(false); // confirm-reject step for acknowledger

  const partyResponse = async (accept, dtype) => {
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/deductions/party-response`, {
        accept_tenant_counter: accept,
        dispute_type: dtype || "orello",
      });
      toast.success(accept
        ? "Counter accepted. Deposit being released."
        : "Counter rejected. The tenant has been notified to decide next.");
      setRejectStep(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const raiseDispute = async (reason = "", evidence = "") => {
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/dispute`, {
        reason: reason || "Disagree with deduction proposal",
        evidence_notes: evidence || "See check-in vs checkout report comparison.",
      });
      toast.success("Dispute raised. ORE-AI will review all evidence instantly.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const acceptOriginalDeduction = async () => {
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/deductions/accept-original`);
      toast.success("Deduction accepted. Your refund will be processed.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;

  const report = checkoutData?.report;
  const checkin = checkinData;
  const deduction = checkoutData?.deduction;
  const isAcknowledger = report?.acknowledger_id === user.id;
  const isBrokerOrAgent = ["agent", "broker_admin"].includes(user.role);

  // Helper — get checkin condition for a room
  const checkinRoom = (roomName) => checkin?.rooms?.find(r => r.room === roomName);

  // ── GATE: settled/completed statuses — show the locked checkout report ──
  if (["completed", "rating_phase1", "rating_phase2", "payout_processing", "awaiting_claim", "settlement_pending"].includes(rental.status)) {
    if (!report) return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">No checkout report on record.</div>;
    return (
      <div className="space-y-4" data-testid="pane-moveout">
        <div className="text-sm text-emerald-700 bg-emerald-50 p-4 rounded-lg flex items-center gap-2">
          <Lock className="h-4 w-4" /> Checkout report locked on {formatDate(report.submitted_at)}
        </div>
        <div className="card-base divide-y divide-gray-100 px-2 py-1">
          {report.rooms?.map((rm, i) => {
            const ci = checkinRoom(rm.room);
            const remark = checkin?.tenant_remarks?.find(r => r.room === rm.room)?.comment;
            return <CheckoutRoomRow key={i} rm={rm} checkinRoom={ci} tenantRemarks={remark} />;
          })}
        </div>
        {report.overall_notes && <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{report.overall_notes}</div>}
      </div>
    );
  }

  // ── GATE: status not in checkout flow ──
  if (!["checkout_pending", "checkout_review", "deduction_review", "deduction_counter", "final_disagreement", "checkout_auto_locked", "dispute_review", "dispute", "external_dispute", "manual_review", "manual_resolved"].includes(rental.status)) {
    if (!["active", "expired_continuing"].includes(rental.status)) {
      return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">End of Tenancy not available yet. Complete agreement and deposit payment first.</div>;
    }
    // Only the broker/agent/landlord can start renewal or end-of-tenancy — not the tenant.
    if (user.role === "tenant") {
      return <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">Your agent or landlord will start the renewal or end-of-tenancy process when the time comes. You'll be notified when it begins.</div>;
    }
    // Renew/End open only within 2 months of the registered lease end.
    // Early Termination is always available (for ending before the lease ends).
    const leaseEnd = rental.lease_end_date ? new Date(rental.lease_end_date) : null;
    const windowOpens = leaseEnd ? new Date(leaseEnd.getFullYear(), leaseEnd.getMonth() - 2, leaseEnd.getDate()) : null;
    const withinEndWindow = rental.status === "expired_continuing" || !leaseEnd || Date.now() >= windowOpens.getTime();
    return (
      <div className="space-y-4" data-testid="pane-moveout">
        <div>
          <h3 className="text-lg font-semibold font-display">What would you like to do?</h3>
          <p className="text-sm text-gray-500 mt-1">
            {withinEndWindow
              ? "Renew the tenancy, begin checkout, or end early."
              : `Renewal and end-of-tenancy open 2 months before the lease ends (${formatDate(rental.lease_end_date)}). You can still end early below.`}
          </p>
        </div>
        {!endChoice && (
          <div className="grid grid-cols-2 gap-3">
            {withinEndWindow && (
              <button onClick={startRenewal} disabled={busy}
                className="border-2 border-emerald-400 bg-emerald-50 text-emerald-700 rounded-xl p-4 text-left hover:bg-emerald-100 transition-colors disabled:opacity-50">
                <div className="font-semibold text-sm">🔄 Renew Tenancy</div>
                <div className="text-xs text-emerald-600 mt-1">Update terms and generate a new agreement to sign</div>
              </button>
            )}
            {withinEndWindow && (
              <button onClick={() => setEndChoice("end")}
                className="border-2 border-red-300 bg-red-50 text-red-700 rounded-xl p-4 text-left hover:bg-red-100 transition-colors">
                <div className="font-semibold text-sm">🚪 End Tenancy</div>
                <div className="text-xs text-red-600 mt-1">Begin the checkout and deposit return process</div>
              </button>
            )}
            <button onClick={earlyTerminate} disabled={busy}
              className="border-2 border-amber-300 bg-amber-50 text-amber-700 rounded-xl p-4 text-left hover:bg-amber-100 transition-colors disabled:opacity-50">
              <div className="font-semibold text-sm">⚠️ Early Termination</div>
              <div className="text-xs text-amber-600 mt-1">End before the lease ends — starts checkout now</div>
            </button>
          </div>
        )}
        {endChoice === "end" && (
          <div className="space-y-3">
            <button onClick={() => setEndChoice(null)} className="text-sm text-gray-500">← Back</button>
            <div>
              <label className="text-sm font-medium">End of Tenancy date</label>
              <input type="date" value={moveoutDate} onChange={(e) => setMoveoutDate(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
            </div>
            <button onClick={triggerMoveout} disabled={busy}
              className="bg-red-500 hover:bg-red-600 btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
              {busy ? "Confirming…" : "Confirm End of Tenancy"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Auto locked ──
  if (rental.status === "checkout_auto_locked") {
    return (
      <div className="text-sm text-amber-700 bg-amber-50 p-4 rounded-lg">
        Checkout report deadline passed. No issues assumed — full deposit will be refunded to tenant.
      </div>
    );
  }

  // ── Submit checkout report ──
  if (rental.status === "checkout_pending" && !report && isCreator) {
    const checkoutDeadline = rental.checkout_deadline ? new Date(rental.checkout_deadline) : null;
    const daysLeft = checkoutDeadline ? Math.ceil((checkoutDeadline - new Date()) / (1000 * 60 * 60 * 24)) : 0;
    const canDefer = !rental.checkout_deferred && daysLeft > 0;

    return (
      <div className="space-y-4" data-testid="pane-moveout">
        <div>
          <h3 className="text-lg font-semibold font-display">Checkout Report</h3>
          <p className="text-sm text-gray-500 mt-1">Document the property condition room by room. {checkoutDeadline && `Deadline: ${formatDate(checkoutDeadline)}`}</p>
        </div>

        {/* PHASE 6: Defer option for extending deadline */}
        {daysLeft <= 3 && daysLeft > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-semibold text-amber-900">⏰ {daysLeft} day{daysLeft !== 1 ? 's' : ''} left to submit</div>
              <p className="text-sm text-amber-700 mt-1">{canDefer ? "Need more time? You can extend the deadline by 4 days, for a total of 7 days (one-time only)." : "Deadline cannot be extended further."}</p>
            </div>
            {canDefer && (
              <button onClick={deferCheckoutDeadline} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap">
                Extend 4 Days
              </button>
            )}
          </div>
        )}

        {checkin && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
            ℹ️ Check-in conditions shown for reference — expand each room below to compare.
          </div>
        )}
        <div className="space-y-2">
          {rooms.map(room => {
            const ci = checkinRoom(room);
            return (
              <div key={room}>
                {ci && !ci.not_applicable && (
                  <div className="text-xs text-gray-400 px-1 mb-0.5">
                    Move-in: <span className={`font-medium ${ci.condition === "excellent" ? "text-emerald-600" : ci.condition === "good" ? "text-blue-600" : ci.condition === "fair" ? "text-amber-600" : "text-red-600"}`}>{ci.condition}</span>
                    {ci.notes && ` — ${ci.notes}`}
                  </div>
                )}
                <RoomCard room={room} value={roomData[room]} onChange={(v) => setRoomData({ ...roomData, [room]: v })} showOfix={false} lockedNa={!!ci?.not_applicable} />
              </div>
            );
          })}
        </div>
        <textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)}
          placeholder="Overall property notes (optional)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={3} />
        {isBrokerOrAgent && (
          <div className="card-base p-4 space-y-2">
            <div className="text-sm font-medium">Who will review deductions?</div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setAcknowledger("self")}
                className={`px-4 py-2 rounded-lg text-sm border ${acknowledger === "self" ? "bg-[#FFF4ED] border-indigo-500 text-indigo-900" : "border-gray-200"}`}>
                Me
              </button>
              <button type="button" onClick={() => setAcknowledger("landlord")}
                className={`px-4 py-2 rounded-lg text-sm border ${acknowledger === "landlord" ? "bg-[#FFF4ED] border-indigo-500 text-indigo-900" : "border-gray-200"}`}>
                Landlord
              </button>
            </div>
          </div>
        )}
        <button onClick={submitCheckout} disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white px-5 py-2.5 rounded-lg font-medium text-sm">
          {busy ? "Submitting…" : "Submit Checkout Report"}
        </button>
      </div>
    );
  }

  // ── Acknowledger — side by side comparison + deduction proposal ──
  if (rental.status === "checkout_review" && report && isAcknowledger) {
    const totalDeduction = Object.values(deductByRoom).reduce((s, d) => s + Math.round(Number(d?.amount || 0) * 100), 0);
    const tenantRefund = Math.max(0, rental.deposit_amount - totalDeduction);
    const setDeduct = (room, val) => setDeductByRoom(prev => {
      const next = { ...prev };
      if (val == null) delete next[room]; else next[room] = val;
      return next;
    });
    return (
      <div className="space-y-5" data-testid="pane-moveout">
        <div>
          <h3 className="text-lg font-semibold font-display">Review Checkout Report</h3>
          <p className="text-sm text-gray-500 mt-1">Expand each room to compare move-in vs move-out. If there's damage, tick "Deduct from the deposit" right there.</p>
        </div>

        {/* Room comparison + inline deduction, room by room */}
        <div className="card-base divide-y divide-gray-100 px-2 py-1">
          {report.rooms?.map((rm, i) => {
            const ci = checkinRoom(rm.room);
            const remark = checkin?.tenant_remarks?.find(r => r.room === rm.room)?.comment;
            return (
              <CheckoutReviewRow key={i} rm={rm} checkinRoom={ci} tenantRemarks={remark}
                deduct={deductByRoom[rm.room]} onDeductChange={(v) => setDeduct(rm.room, v)} />
            );
          })}
        </div>

        {/* Running total */}
        {totalDeduction > 0 && (
          <div className="card-base p-3 space-y-1">
            <div className="flex justify-between text-sm"><span className="text-gray-500">Total deposit</span><span>{formatINR(rental.deposit_amount)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Total deductions</span><span className="text-red-600">− {formatINR(totalDeduction)}</span></div>
            <div className="flex justify-between text-sm font-semibold border-t pt-1"><span>Tenant refund</span><span className="text-emerald-600">{formatINR(tenantRefund)}</span></div>
          </div>
        )}

        <div className="flex gap-3">
          {totalDeduction > 0 && (
            <button onClick={proposeDeductions} disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-5 py-2.5 rounded-lg font-medium text-sm">
              Send Deduction Proposal
            </button>
          )}
          <button onClick={confirmNoDeductions} disabled={busy} className="border border-emerald-500 text-emerald-600 px-5 py-2.5 rounded-lg font-medium text-sm">
            No Deductions — Full Refund
          </button>
        </div>
      </div>
    );
  }

  // ── Tenant — review deduction proposal ──
  if (rental.status === "deduction_review" && user.role === "tenant" && deduction) {
    const responseDeadline = deduction.deduction_response_deadline ? new Date(deduction.deduction_response_deadline) : null;
    const hoursLeft = responseDeadline ? Math.ceil((responseDeadline - new Date()) / (1000 * 60 * 60)) : 0;
    const isUrgent = hoursLeft <= 24;

    return (
      <div className="space-y-5" data-testid="pane-moveout">
        <div>
          <h3 className="text-lg font-semibold font-display">Deduction Proposal</h3>
          <p className="text-sm text-gray-500 mt-1">For each item choose <strong>Agree</strong> or <strong>Disagree</strong>. If you disagree, propose a fair amount and add a reason with photos.</p>
        </div>

        {/* PHASE 7: 48-72 hour response deadline countdown */}
        {responseDeadline && (
          <div className={`rounded-lg p-4 flex items-start justify-between gap-3 ${isUrgent ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
            <div className="flex-1">
              <div className={`font-semibold ${isUrgent ? "text-red-900" : "text-amber-900"}`}>⏰ {hoursLeft} hours left to respond</div>
              <p className={`text-sm mt-1 ${isUrgent ? "text-red-700" : "text-amber-700"}`}>Respond by {responseDeadline.toLocaleDateString()} at {responseDeadline.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
            </div>
          </div>
        )}

        {/* Decision table — one row per claimed item */}
        <div className="card-base p-0 overflow-x-auto">
          {/* Column headers (shown on wider screens) */}
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_150px_110px_170px] gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            <span>Item</span>
            <span className="text-center">Move-in → out</span>
            <span className="text-right">Claimed</span>
            <span className="text-center">Your decision</span>
          </div>
          {deduction.items?.map((item, i) => {
            const resp = tenantResponse[item.room] || {};
            const ci = checkinRoom(item.room);
            const co = report?.rooms?.find(r => r.room === item.room);
            const decided = resp.decision;
            return (
              <div key={i} className={`border-b border-gray-100 last:border-0 ${decided === "disagree" ? "bg-amber-50/40" : decided === "agree" ? "bg-emerald-50/30" : ""}`}>
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px_110px_170px] gap-2 items-center px-4 py-3">
                  {/* Item */}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{item.room}</div>
                    <div className="text-xs text-gray-500">{item.reason}</div>
                  </div>
                  {/* Move-in → move-out condition */}
                  <div className="flex items-center sm:justify-center gap-1.5 text-xs">
                    {ci ? <span className={`pill text-xs ${condPill(ci.condition)}`}>{ci.condition}</span> : <span className="text-gray-300">—</span>}
                    <span className="text-gray-300">→</span>
                    {co ? <span className={`pill text-xs ${condPill(co.condition)}`}>{co.condition}</span> : <span className="text-gray-300">—</span>}
                  </div>
                  {/* Claimed amount — prominent */}
                  <div className="sm:text-right">
                    <span className="sm:hidden text-xs text-gray-400 mr-1">Claimed:</span>
                    <span className="text-base font-bold text-red-600">{formatINR(item.amount)}</span>
                  </div>
                  {/* Decision — explicit Agree / Disagree */}
                  <div className="grid grid-cols-2 gap-1.5">
                    <button type="button"
                      onClick={() => setTenantResponse({ ...tenantResponse, [item.room]: { ...resp, decision: "agree", amount: item.amount / 100 } })}
                      className={`py-1.5 rounded-lg text-xs font-semibold border transition-colors ${decided === "agree" ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-200 text-gray-500 hover:border-emerald-300"}`}>
                      ✓ Agree
                    </button>
                    <button type="button"
                      onClick={() => setTenantResponse({ ...tenantResponse, [item.room]: { ...resp, decision: "disagree", amount: resp.amount ?? "" } })}
                      className={`py-1.5 rounded-lg text-xs font-semibold border transition-colors ${decided === "disagree" ? "bg-red-500 border-red-500 text-white" : "border-gray-200 text-gray-500 hover:border-red-300"}`}>
                      ✗ Disagree
                    </button>
                  </div>
                </div>

                {/* Disagree → counter amount + reason + evidence */}
                {decided === "disagree" && (
                  <div className="px-4 pb-4 space-y-3">
                    {(ci || co) && (
                      <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-3 border border-gray-100 text-xs">
                        <div className="space-y-1.5">
                          <div className="text-gray-400 font-semibold uppercase tracking-wide">Move-in</div>
                          {ci?.notes && <div className="text-gray-500 italic">{ci.notes}</div>}
                          <MediaGrid urls={ci?.photo_urls} room={`${item.room} move-in`} />
                        </div>
                        <div className="space-y-1.5">
                          <div className="text-gray-400 font-semibold uppercase tracking-wide">Move-out</div>
                          {co?.notes && <div className="text-gray-500 italic">{co.notes}</div>}
                          <MediaGrid urls={co?.photo_urls} room={`${item.room} move-out`} />
                        </div>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1 font-medium">Your fair amount (₹)</div>
                        <input type="number" value={resp.amount ?? ""} min={0} max={item.amount / 100}
                          onChange={(e) => setTenantResponse({ ...tenantResponse, [item.room]: { ...resp, decision: "disagree", amount: e.target.value } })}
                          placeholder={`0 – ${(item.amount / 100).toLocaleString("en-IN")}`}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                        <div className="text-[11px] text-gray-400 mt-1">Enter ₹0 if you reject this deduction entirely.</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1 font-medium">Reason <span className="text-red-500">*</span></div>
                        <textarea value={resp.comment || ""} rows={2}
                          onChange={(e) => setTenantResponse({ ...tenantResponse, [item.room]: { ...resp, decision: "disagree", comment: e.target.value } })}
                          placeholder="Why do you disagree? Reference the move-in condition, pre-existing damage, etc."
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1 font-medium">Evidence photos <span className="text-gray-400 font-normal">(recommended)</span></div>
                      <PhotoUpload
                        value={tenantPhotos[item.room] || []}
                        onChange={(p) => setTenantPhotos({ ...tenantPhotos, [item.room]: p })}
                        label="Add photos / videos as evidence"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Live summary — updates as you decide */}
        {(() => {
          const items = deduction.items || [];
          const allDecided = items.length > 0 && items.every(it => tenantResponse[it.room]?.decision);
          const yourDeduction = items.reduce((s, it) => {
            const resp = tenantResponse[it.room];
            if (!resp?.decision) return s;
            const amt = resp.decision === "agree"
              ? it.amount
              : Math.max(0, Math.min(it.amount, Math.round(Number(resp.amount || 0) * 100)));
            return s + amt;
          }, 0);
          const yourRefund = Math.max(0, rental.deposit_amount - yourDeduction);
          return (
            <div className="card-base p-4 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-gray-500">Total deposit</span><span>{formatINR(rental.deposit_amount)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Owner claimed</span><span className="text-gray-500">− {formatINR(deduction.total_deduction)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-700 font-medium">You accept deducting</span><span className="text-red-600 font-semibold">− {formatINR(yourDeduction)}</span></div>
              <div className="flex justify-between text-base font-semibold border-t pt-2 mt-1"><span>You would get back</span><span className="text-emerald-600">{formatINR(yourRefund)}</span></div>
              {!allDecided && <div className="text-xs text-amber-600 pt-1">Choose Agree or Disagree for every item to submit.</div>}
            </div>
          );
        })()}

        <button onClick={submitTenantResponse} disabled={busy} className="bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-5 py-2.5 rounded-lg font-medium text-sm">
          {busy ? "Submitting…" : "Submit Response"}
        </button>
      </div>
    );
  }

  // ── Acknowledger — review tenant counter ──
  if (rental.status === "deduction_counter" && isAcknowledger) {
    const theirTotal = deduction?.total_deduction || 0;
    const tenantTotal = Object.values(deduction?.tenant_response?.tenant_amounts || {}).reduce((s, v) => s + v, 0);
    const tenantRefund = Math.max(0, rental.deposit_amount - tenantTotal);
    return (
      <div className="space-y-5" data-testid="pane-moveout">
        <h3 className="text-lg font-semibold font-display">Tenant's Counter Proposal</h3>

        {/* Side by side summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card-base p-4 space-y-1">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Your claim</div>
            {deduction?.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-600">{item.room}</span>
                <span>{formatINR(item.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
              <span>Total</span><span className="text-red-600">{formatINR(theirTotal)}</span>
            </div>
          </div>
          <div className="card-base p-4 space-y-1">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Tenant's response</div>
            {deduction?.items?.map((item, i) => {
              const tenantAmt = deduction?.tenant_response?.tenant_amounts?.[item.room] || 0;
              const fullAgree = tenantAmt >= item.amount;
              const rejected = tenantAmt <= 0;
              return (
                <div key={i} className="flex justify-between text-sm gap-2">
                  <span className="text-gray-600 truncate">{item.room}</span>
                  <span className={`flex-shrink-0 ${fullAgree ? "text-emerald-600" : rejected ? "text-red-500" : "text-amber-600"}`}>
                    {fullAgree ? `Agreed ${formatINR(tenantAmt)}` : rejected ? "Rejected" : `Counter ${formatINR(tenantAmt)}`}
                  </span>
                </div>
              );
            })}
            <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
              <span>Total</span><span className="text-emerald-600">{formatINR(tenantTotal)}</span>
            </div>
          </div>
        </div>

        <div className="card-base p-4 space-y-1">
          <div className="flex justify-between text-sm"><span className="text-gray-500">Total deposit</span><span>{formatINR(rental.deposit_amount)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">If you accept tenant's counter</span><span>Tenant gets {formatINR(tenantRefund)}</span></div>
        </div>

        {deduction?.tenant_response?.notes && (
          <div className="card-base p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-2">Tenant's comments</div>
            {deduction.tenant_response.notes.split(" | ").map((note, i) => (
              <div key={i} className="text-sm text-gray-700 mb-1">{note}</div>
            ))}
          </div>
        )}
        {deduction?.tenant_response?.photo_urls?.length > 0 && (
          <div className="card-base p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-2">Tenant's evidence photos</div>
            <div className="grid grid-cols-3 gap-2">
              {deduction.tenant_response.photo_urls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer">
                  <img src={url} className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:opacity-90" alt="" />
                </a>
              ))}
            </div>
          </div>
        )}

        {!rejectStep ? (
          <div className="flex gap-3">
            <button onClick={() => partyResponse(true)} disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium">
              ✓ Accept Counter
            </button>
            <button onClick={() => setRejectStep(true)} disabled={busy}
              className="border border-red-300 text-red-600 hover:bg-red-50 px-5 py-2.5 rounded-lg text-sm font-medium">
              ✗ Reject Counter
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <button onClick={() => setRejectStep(false)} className="text-sm text-gray-500">← Back</button>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="font-semibold text-gray-800 mb-1">Reject the tenant's counter?</div>
              <p className="text-sm text-gray-600">
                Your claim of <strong>{formatINR(theirTotal)}</strong> stays on record. The tenant is
                notified and reviews it next — they can accept it and settle, or take it to a dispute
                where ORE-AI weighs all the evidence. You'll be kept in the loop on the outcome.
              </p>
            </div>
            <button onClick={() => partyResponse(false)} disabled={busy}
              className="w-full bg-red-500 hover:bg-red-600 btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
              {busy ? "Submitting…" : "Confirm — reject counter"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Dispute in progress — tenant or landlord view ──
  if (["dispute_review", "dispute", "external_dispute", "manual_review", "manual_resolved"].includes(rental.status)) {
    const dispute = checkoutData?.dispute;
    const s = checkoutData?.settlement;
    const isExternal = rental.status === "external_dispute";
    const oreReviewed = dispute?.status === "ore_reviewed";
    const myAccepted = user.role === "tenant" ? dispute?.tenant_accepted : dispute?.landlord_accepted;

    return (
      <div className="space-y-4" data-testid="pane-moveout">

        {/* ORE-AI reviewing / done banner — only during the ORE-AI phase.
            Once it's escalated to manual review / resolved, those blocks below
            take over (this stops the stale "Analysing…" showing at manual_resolved). */}
        {!isExternal && (dispute?.status === "pending" || dispute?.status === "ore_reviewed") && (
          oreReviewed ? (
            <div className="card-base p-5">
              <div className="flex items-center gap-2 font-semibold text-indigo-500">
                <span>🤖</span> ORE-AI has reviewed your dispute
                {dispute?.ore_ai_confidence && (
                  <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600">
                    {dispute.ore_ai_confidence} confidence
                  </span>
                )}
              </div>
              {/* ORE-AI verdict */}
              <div className="mt-3 bg-white rounded-xl border border-indigo-100 p-4 space-y-3">
                <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">ORE-AI Verdict</div>
                <p className="text-sm text-gray-800 font-medium leading-relaxed">{dispute.orello_suggestion}</p>
              </div>
              {/* Item-by-item breakdown */}
              {dispute.ore_ai_item_analysis?.length > 0 && (
                <div className="mt-3 bg-white rounded-xl border border-indigo-100 p-4 space-y-3">
                  <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">Item Analysis</div>
                  <div className="grid grid-cols-3 text-xs text-gray-400 font-medium pb-1 border-b border-gray-100">
                    <span>Item</span>
                    <span className="text-right">Landlord claimed</span>
                    <span className="text-right">ORE-AI deducts</span>
                  </div>
                  {dispute.ore_ai_item_analysis.map((a, i) => (
                    <div key={i} className="border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                      <div className="grid grid-cols-3 items-start text-sm gap-2">
                        <span className="font-medium text-gray-800 leading-snug">{a.room ? `${a.room} — ` : ""}{a.item}</span>
                        <span className="text-right text-gray-500">{formatINR(a.claimed)}</span>
                        <span className={`text-right font-semibold ${a.awarded === 0 ? "text-emerald-600" : a.awarded < a.claimed ? "text-amber-600" : "text-red-600"}`}>
                          {a.awarded === 0 ? "Nil — returned to tenant" : formatINR(a.awarded) + " to landlord"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1 space-y-0.5 col-span-3">
                        <div>• {a.breakdown?.rate_note}</div>
                        <div>• {a.breakdown?.wear_note}</div>
                        <div>• {a.breakdown?.cond_note}</div>
                        {a.breakdown?.evid_note && <div className="text-indigo-400">• {a.breakdown.evid_note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Settlement summary — clear who gets what */}
              <div className="mt-3 rounded-xl overflow-hidden border border-gray-200">
                <div className="flex justify-between text-sm px-4 py-3 bg-gray-50">
                  <span className="text-gray-500">Total deposit held</span>
                  <span className="font-medium">{formatINR(rental.deposit_amount)}</span>
                </div>
                <div className="flex justify-between text-sm px-4 py-3 border-t border-gray-100 bg-red-50">
                  <div>
                    <span className="font-medium text-red-700">Landlord receives</span>
                    <p className="text-xs text-red-400 mt-0.5">Justified deduction approved by ORE-AI</p>
                  </div>
                  <span className="font-bold text-red-600">{formatINR(dispute.orello_deduction_suggestion)}</span>
                </div>
                <div className="flex justify-between text-sm px-4 py-3 border-t border-gray-100 bg-emerald-50">
                  <div>
                    <span className="font-medium text-emerald-700">Tenant receives</span>
                    <p className="text-xs text-emerald-400 mt-0.5">Refunded back from the security deposit</p>
                  </div>
                  <span className="font-bold text-emerald-600">{formatINR(dispute.orello_refund_suggestion)}</span>
                </div>
              </div>
              {/* Response buttons */}
              {!myAccepted ? (
                <div className="mt-3 space-y-2">
                  <button onClick={acceptOrelloDecision} disabled={busy}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                    ✓ Accept ORE-AI Decision
                  </button>
                  <button onClick={requestManualReview} disabled={busy}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                    👤 Not satisfied? Request an Orello human review
                    <span className="block text-xs font-normal opacity-80 mt-0.5">Free · final binding decision in 7 working days</span>
                  </button>
                  <button onClick={takeExternal} disabled={busy}
                    className="w-full border border-gray-400 bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                    ⚖️ Take to External Court / Arbitration
                  </button>
                </div>
              ) : (
                <div className="mt-3 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  ✓ Your response recorded. Waiting for the other party.
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                ORE-AI analysed deduction claims, check-in/checkout conditions, and 2026 Indian market rates.
              </p>
            </div>
          ) : (
            <div className="card-base p-5">
              <div className="flex items-center gap-2 font-semibold text-indigo-500">
                <span>🤖</span> ORE-AI is reviewing your dispute
                <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600 animate-pulse">
                  Analysing…
                </span>
              </div>
              <p className="text-sm mt-2 text-indigo-700">
                ORE-AI is analysing the deduction claims, property conditions, and market rates to give a fair verdict. This is instant — the result is usually ready in a few seconds.
              </p>
              <p className="text-xs text-indigo-400 mt-1">This page will update automatically with the verdict.</p>
            </div>
          )
        )}

        {/* Manual review in progress */}
        {rental.status === "manual_review" && (
          <div className="card-base p-5 space-y-3">
            <div className="flex items-center gap-2 font-semibold text-orange-500">
              <span>👤</span> Orello Manual Review in Progress
              <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 animate-pulse">
                Under Review
              </span>
            </div>
            {dispute?.manual_auto_escalated && (
              <div className="text-xs text-orange-700 bg-orange-100 border border-orange-200 rounded-lg px-3 py-2">
                ORE-AI couldn't reach a confident automatic verdict on this case, so it's been passed to Orello's review team — <strong>at no extra charge</strong>.
              </div>
            )}
            <p className="text-sm text-orange-700">
              Our team is carefully reviewing all evidence — deduction claims, check-in/checkout reports, photos, remarks, and local market standards — to give a final binding decision.
            </p>
            {dispute?.manual_review_due_at && (
              <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-100 px-3 py-2 rounded-lg">
                <span>📅</span>
                Final decision by: <strong>{new Date(dispute.manual_review_due_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</strong>
              </div>
            )}
            <p className="text-xs text-orange-400">This is the final review. After this, only external resolution is available.</p>
          </div>
        )}

        {/* Manual resolved — final Orello decision */}
        {rental.status === "manual_resolved" && dispute && (() => {
          const myAcc = user.role === "tenant" ? dispute.tenant_accepted : dispute.landlord_accepted;
          return (
            <div className="card-base p-5 space-y-3">
              <div className="flex items-center gap-2 font-semibold text-orange-500">
                <span>👤</span> Orello Team — Final Decision
                <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full bg-orange-200 text-orange-800">FINAL</span>
              </div>
              <p className="text-sm text-orange-800 leading-relaxed font-medium">{dispute.manual_review_verdict}</p>
              {dispute.manual_review_reasoning && (
                <p className="text-xs text-orange-700 leading-relaxed">{dispute.manual_review_reasoning}</p>
              )}
              <div className="rounded-xl overflow-hidden border border-orange-200">
                <div className="flex justify-between text-sm px-4 py-3 bg-red-50">
                  <span className="font-medium text-red-700">Landlord receives</span>
                  <span className="font-bold text-red-600">{formatINR(dispute.manual_review_deduction || 0)}</span>
                </div>
                <div className="flex justify-between text-sm px-4 py-3 border-t border-orange-100 bg-emerald-50">
                  <span className="font-medium text-emerald-700">Tenant receives</span>
                  <span className="font-bold text-emerald-600">{formatINR(dispute.manual_review_refund || 0)}</span>
                </div>
              </div>
              {!myAcc ? (
                <>
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    This is Orello's <strong>final, binding decision</strong> — it cannot be re-reviewed by Orello.
                    Accept it to settle and receive your amount, or, if you disagree, take it to external court / arbitration.
                  </div>
                  <div className="flex gap-2">
                    <button onClick={acceptOrelloDecision} disabled={busy}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                      ✓ Accept &amp; settle
                    </button>
                    <button onClick={takeExternal} disabled={busy}
                      className="flex-1 border border-gray-400 bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                      ⚖️ Take externally
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  ✓ Your acceptance is recorded. Waiting for the other party — Orello disburses once <strong>both</strong> accept.
                  If either party takes it external instead, the disputed amount stays held pending proof.
                </div>
              )}
            </div>
          );
        })()}

        {isExternal && (() => {
          const ep = dispute?.external_proof;
          return (
            <div className="card-base p-5 space-y-3">
              <div className="font-semibold text-gray-800">⚖️ External resolution in progress</div>
              <p className="text-sm text-gray-600">
                The disputed amount stays <strong>held by Orello</strong> until the external outcome is provided.
              </p>
              {ep ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                  ✓ Proof submitted{ep.submitted_by_name ? ` by ${ep.submitted_by_name}` : ""} on {formatDate(ep.submitted_at)}.
                  Orello is validating it and will disburse the held amount to each party within <strong>5–7 working days</strong>.
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <div className="text-sm font-medium text-gray-800">Resolved in court / arbitration? Submit the proof</div>
                  <div className="text-xs text-gray-500">
                    Upload the court order or signed settlement. Once Orello validates it, the held amount is
                    disbursed to each party in 5–7 working days. Either party can submit.
                  </div>
                  <PhotoUpload value={proofFiles} onChange={setProofFiles} label="Add proof (court order / signed settlement)"
                    accept="image/*,video/*,application/pdf" hint="Photos, videos, or PDF." />
                  <textarea value={proofNote} onChange={(e) => setProofNote(e.target.value)} rows={2}
                    placeholder="Optional: describe the outcome / the agreed split"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" />
                  <button onClick={submitExternalProof} disabled={busy}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                    {busy ? "Submitting…" : "Submit external settlement proof"}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Deposit split — undisputed claim + disputed hold */}
        {s && (
          <div className="card-base p-5 space-y-3">
            <div className="text-sm font-semibold text-gray-700">Your deposit status</div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total deposit</span>
              <span>{formatINR(rental.deposit_amount)}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold border-t pt-2">
              <span className="text-amber-700">Held by Orello (disputed)</span>
              <span className="text-amber-600">{formatINR(s.disputed_amount_held)}</span>
            </div>
            {/* Undisputed claim — claimable once, while the disputed amount stays held */}
            {s.tenant_refund_amount > 0 && user.role === "tenant" && (
              s.undisputed_claimed ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
                  ✓ Undisputed {formatINR(s.tenant_refund_amount)} claimed — sent via Cashfree {s.transfer_mode || "IMPS"}, arriving {s.transfer_eta || "within a few minutes"}.
                  The disputed amount stays held {isExternal ? "until external resolution proof is submitted and validated" : "pending review"}.
                </div>
              ) : showClaim ? (
                <TenantRefundClaimWidget rid={rental.id} amount={s.tenant_refund_amount} onDone={onDone} />
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-semibold text-emerald-800">Undisputed amount — ready to claim</div>
                      <div className="text-xs text-emerald-600 mt-0.5">
                        This amount is not in dispute. Claim it to your bank now while the disputed amount stays held
                        {isExternal ? " pending external resolution." : " pending review."}
                      </div>
                    </div>
                    <span className="text-lg font-bold text-emerald-700 ml-3 flex-shrink-0">{formatINR(s.tenant_refund_amount)}</span>
                  </div>
                  <button
                    onClick={() => setShowClaim(true)}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium">
                    Claim {formatINR(s.tenant_refund_amount)} now →
                  </button>
                </div>
              )
            )}
          </div>
        )}

        {/* External-resolution Terms & Conditions modal */}
        {showExternalTerms && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && setShowExternalTerms(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 font-semibold text-gray-900">
                <span>⚖️</span> External resolution — please read
              </div>
              <div className="text-sm text-gray-600 space-y-2">
                <p>By choosing external resolution (court or arbitration), you agree that:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>The disputed amount stays <strong>held by Orello</strong> — it is not released to either party until the external outcome is provided.</li>
                  <li>Once resolved externally, <strong>either party can return to the Settlement tab and submit proof</strong> of the outcome (court order / signed agreement).</li>
                  <li>Orello will <strong>validate the proof and disburse</strong> the deposit to each party accordingly within <strong>5–7 working days</strong>.</li>
                  <li>This step is <strong>final</strong> — Orello provides no further internal review.</li>
                </ul>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg p-3">
                <input type="checkbox" checked={extTermsAgreed} onChange={(e) => setExtTermsAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#F97316]" />
                <span className="text-sm text-gray-700">I understand and agree to these terms.</span>
              </label>
              <div className="flex gap-2">
                <button onClick={() => setShowExternalTerms(false)} disabled={busy}
                  className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-medium">
                  Cancel
                </button>
                <button onClick={confirmTakeExternal} disabled={busy || !extTermsAgreed}
                  className="flex-1 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                  {busy ? "Processing…" : "Agree & take external"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Tenant — landlord rejected counter, tenant must choose: accept or dispute ──
  if (rental.status === "final_disagreement" && user.role === "tenant") {
    const totalClaimed = deduction?.total_deduction || 0;
    const tenantCounter = deduction?.tenant_counter_amount || 0;
    const undisputedIfDispute = Math.max(0, rental.deposit_amount - totalClaimed);
    const rejectedBy = report?.acknowledger_role === "landlord" ? "The landlord" : "The broker";
    return (
      <div className="space-y-4" data-testid="pane-moveout">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="font-semibold text-amber-800">Rejected by {report?.acknowledger_role === "landlord" ? "landlord" : "broker"}</div>
          <p className="text-sm text-amber-700 mt-2">
            {rejectedBy} rejected your counter. You offered <strong>{formatINR(tenantCounter)}</strong> and
            they insist on their original claim of <strong>{formatINR(totalClaimed)}</strong>.
            It's now your decision:
          </p>
        </div>

        {/* Deduction breakdown */}
        {deduction?.items?.length > 0 && (
          <div className="card-base p-4 space-y-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner's original claim</div>
            {deduction.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-600">{item.room} — {item.reason}</span>
                <span className="font-medium">{formatINR(item.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold border-t pt-2">
              <span className="text-gray-700">Total deduction</span>
              <span className="text-red-600">{formatINR(totalClaimed)}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold">
              <span className="text-gray-700">You get back</span>
              <span className="text-emerald-600">{formatINR(deduction.tenant_refund || (rental.deposit_amount - totalClaimed))}</span>
            </div>
          </div>
        )}

        {/* Two choices */}
        <div className="grid grid-cols-1 gap-3">
          {/* Option A — Accept */}
          <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
            <div className="font-medium text-emerald-800 text-sm">Option A — Accept the owner's deduction</div>
            <p className="text-xs text-emerald-700 mt-1">
              You accept ₹{(totalClaimed/100).toLocaleString("en-IN")} as deduction.
              You get back <strong>{formatINR(deduction?.tenant_refund || (rental.deposit_amount - totalClaimed))}</strong> within 3-5 days.
            </p>
            <button onClick={acceptOriginalDeduction} disabled={busy}
              className="mt-3 w-full bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              ✓ Accept & settle
            </button>
          </div>

          {/* Option B — Dispute */}
          <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4">
            <div className="font-medium text-indigo-800 text-sm">Option B — Raise a dispute with ORE-AI</div>
            <p className="text-xs text-indigo-700 mt-1">
              ORE-AI will independently review all evidence, reports, photos, and local market rates instantly and give a fair verdict.
              You can claim your undisputed amount (<strong>{formatINR(undisputedIfDispute)}</strong>) right away.
            </p>
            <button onClick={() => raiseDispute("Owner rejected my counter. I disagree with the full claim.", "")}
              disabled={busy}
              className="mt-3 w-full bg-indigo-600 hover:bg-indigo-700 btn-press text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              🤖 Raise dispute — let ORE-AI decide
            </button>
          </div>

          {/* Option C — External */}
          <div className="border border-gray-300 bg-gray-50 rounded-xl p-4">
            <div className="font-medium text-gray-800 text-sm">Option C — Resolve externally</div>
            <p className="text-xs text-gray-600 mt-1">
              Take it to court or external arbitration instead of ORE-AI. Orello releases your
              undisputed amount (<strong>{formatINR(undisputedIfDispute)}</strong>) now and holds the
              disputed <strong>{formatINR(totalClaimed)}</strong> until an external decision is provided.
            </p>
            <button onClick={goExternal} disabled={busy}
              className="mt-3 w-full border border-gray-400 text-gray-700 hover:bg-gray-100 btn-press px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              ⚖️ Resolve externally
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Waiting / observer state ──
  return (
    <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
      {rental.status === "checkout_pending" ? "Waiting for checkout report to be submitted." :
       rental.status === "checkout_review" ? "Checkout report submitted. Waiting for deduction review." :
       rental.status === "deduction_review" ? "Deduction proposal sent. Waiting for tenant response." :
       rental.status === "deduction_counter" ? "Tenant has countered. Waiting for acknowledger response." :
       rental.status === "final_disagreement" ? "Awaiting tenant's final decision." :
       "End of Tenancy process in progress."}
    </div>
  );
}

// ─── Case-closed feedback — auto-pops up, can be re-opened ───────────────────
function CaseClosedFeedbackPrompt({ rentalId, hasDispute, hasManualReview }) {
  const [open, setOpen]         = useState(false);
  const [done, setDone]         = useState(false);    // submitted
  const [skipped, setSkipped]   = useState(false);    // closed without submitting

  // Auto-open after a short delay once the settlement screen settles
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => { setOpen(false); setSkipped(true); };
  const handleDone  = () => { setOpen(false); setDone(true); };

  return (
    <>
      {open && (
        <FeedbackModal
          type="case_closed"
          rentalId={rentalId}
          hasDispute={hasDispute}
          hasManualReview={hasManualReview}
          onClose={handleClose}
          onSubmitted={handleDone}
        />
      )}

      {/* After skipping: small re-open nudge */}
      {skipped && !done && (
        <div className="bg-[#FFF4ED] border border-orange-100 rounded-xl p-3 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-700">Share your feedback?</span>
          <button
            onClick={() => { setOpen(true); setSkipped(false); }}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            Rate experience
          </button>
        </div>
      )}
      {done && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-700 text-center">
          🙏 Thank you for your feedback!
        </div>
      )}
    </>
  );
}

// ─── Settlement Pane ──────────────────────────────────────────────────────────

function SettlementPane({ r, onDone }) {
  const { user } = useAuth();
  const s = r.settlement;
  const d = r.dispute;
  const deduction = r.deduction;
  const [busy, setBusy] = useState(false);

  const acceptOrelloDecision = async () => {
    setBusy(true);
    try { await api.post(`/rentals/${r.id}/dispute/accept`); toast.success("Decision accepted. Settlement finalised."); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const goExternal = async () => {
    setBusy(true);
    try { await api.post(`/rentals/${r.id}/dispute/external`); toast.success("Marked for external resolution. Disputed amount held by Orello."); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestManualReview = async () => {
    if (!window.confirm("Request a free Orello human review? Our team gives a final, binding decision within 7 working days.")) return;
    setBusy(true);
    try {
      await api.post(`/rentals/${r.id}/dispute/manual-review`);
      toast.success("Human review requested. Orello's team will decide within 7 working days.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ── PHASE 8: Awaiting claim (tenant needs to claim refund) ──
  if (r.status === "awaiting_claim" && s?.status === "agreed") {
    const tenantRefund = s?.tenant_refund_amount || 0;
    const isCurrentUserTenant = user?.role === "tenant";
    return (
      <div className="space-y-4" data-testid="pane-settlement-phase8">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-center gap-2 text-blue-700 font-semibold text-lg">
            <Check className="h-5 w-5" /> Settlement Agreed
          </div>
          <p className="text-sm text-blue-600 mt-1">Both parties agreed on the settlement. Tenant can now claim their refund.</p>
        </div>

        {/* Money breakdown */}
        <div className="card-base p-5 space-y-3">
          <div className="text-sm font-semibold text-gray-700">Settlement breakdown</div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Original deposit</span>
            <span className="font-medium">{formatINR(r.deposit_amount)}</span>
          </div>
          {s?.landlord_deduction_amount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Landlord deductions</span>
              <span className="font-medium text-red-600">− {formatINR(s.landlord_deduction_amount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm border-t pt-3">
            <span className="font-semibold">Your refund</span>
            <span className="font-bold text-emerald-600 text-lg">{formatINR(tenantRefund)}</span>
          </div>
        </div>

        {/* Tenant refund claim widget */}
        {isCurrentUserTenant && (
          <TenantRefundClaimWidget rid={r.id} amount={tenantRefund} onDone={onDone} />
        )}

        {/* Deduction reason if any */}
        {s?.deduction_reason && (
          <div className="card-base p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Settlement notes</div>
            <div className="text-sm text-gray-700">{s.deduction_reason}</div>
          </div>
        )}

        {/* Landlord payout widget if there are deductions */}
        {user?.role === "landlord" && s?.landlord_deduction_amount > 0 && (
          <LandlordPayoutWidget rid={r.id} amount={s.landlord_deduction_amount} />
        )}
      </div>
    );
  }

  // ── PHASE 8: Payout processing (refund in transit) ──
  if (r.status === "payout_processing" && s?.status === "agreed") {
    const tenantRefund = s?.tenant_refund_amount || 0;
    return (
      <div className="space-y-4" data-testid="pane-settlement-payout">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-lg">
            <Check className="h-5 w-5" /> Refund Processing
          </div>
          <p className="text-sm text-amber-600 mt-1">Your refund of {formatINR(tenantRefund)} was sent automatically via <strong>Cashfree {s?.transfer_mode || "Payouts"}</strong> — arriving {s?.transfer_eta || "shortly"} to your bank.</p>
          {s?.cf_transfer_id && (
            <p className="text-xs text-amber-500 mt-1">Cashfree ref: {s.cf_transfer_id}</p>
          )}
        </div>

        {/* Money breakdown */}
        <div className="card-base p-5 space-y-3">
          <div className="text-sm font-semibold text-gray-700">Refund Details</div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Amount</span>
            <span className="font-bold text-emerald-600">{formatINR(tenantRefund)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Status</span>
            <span className="font-medium text-amber-600">Processing</span>
          </div>
          <div className="text-xs text-gray-400 mt-3">Expected within 5 business days</div>
        </div>
      </div>
    );
  }

  // ── Completed / settled ──
  if (r.status === "completed" || s?.status === "agreed" || s?.status === "resolved") {
    const agreedDeduction = s?.landlord_deduction_amount || 0;
    const tenantRefund = s?.tenant_refund_amount || 0;
    const isDisputed = !!d;
    return (
      <div className="space-y-4" data-testid="pane-settlement">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold text-lg">
            <ShieldCheck className="h-5 w-5" /> Settlement Complete
          </div>
          <p className="text-sm text-emerald-600 mt-1">Both parties agreed. Funds are being released.</p>
        </div>

        {/* Money breakdown */}
        <div className="card-base p-5 space-y-3">
          <div className="text-sm font-semibold text-gray-700">Final breakdown</div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Total deposit held</span>
            <span className="font-medium">{formatINR(r.deposit_amount)}</span>
          </div>
          {agreedDeduction > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Deductions to landlord</span>
              <span className="font-medium text-red-600">− {formatINR(agreedDeduction)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm border-t pt-3">
            <span className="font-semibold">Tenant refund</span>
            <span className="font-bold text-emerald-600 text-lg">{formatINR(tenantRefund)}</span>
          </div>
          {agreedDeduction > 0 && (
            <div className="text-xs text-gray-400">Refund arrives in 5–7 working days via the original payment method.</div>
          )}
        </div>

        {/* Deduction reason */}
        {s?.deduction_reason && (
          <div className="card-base p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Settlement notes</div>
            <div className="text-sm text-gray-700">{s.deduction_reason}</div>
          </div>
        )}

        {/* Landlord payout widget */}
        {user?.role === "landlord" && agreedDeduction > 0 && (
          <LandlordPayoutWidget rid={r.id} amount={agreedDeduction} />
        )}
        {/* Dispute summary if there was one */}
        {isDisputed && (
          <div className="card-base p-5 space-y-3 border-orange-100">
            <div className="text-sm font-semibold text-orange-800">Dispute was raised</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Tenant's reason</div>
            <div className="text-sm text-gray-700">{d.reason}</div>
            {d.orello_suggestion && (
              <>
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-2">Orello's decision</div>
                <div className="text-sm text-gray-700 bg-orange-50 p-3 rounded-lg">{d.orello_suggestion}</div>
              </>
            )}
            <div className="text-xs text-gray-400">Resolved {formatDate(d.resolved_at)}</div>
          </div>
        )}

        {/* Case-closed feedback prompt */}
        {!hasFeedback("case_closed", r.id) && (
          <CaseClosedFeedbackPrompt
            rentalId={r.id}
            hasDispute={isDisputed}
            hasManualReview={d?.status === "manual_resolved"}
          />
        )}
      </div>
    );
  }

  // ── ORE-AI / Orello has given a dispute decision — both parties must respond ──
  if (["ore_reviewed", "resolved"].includes(d?.status) && r.status !== "completed" && r.status !== "awaiting_claim") {
    const disputedAmount = d.orello_deduction_suggestion || 0;
    const refundAmount = d.orello_refund_suggestion || 0;
    const hasResponded = (user.role === "tenant" && d.tenant_accepted !== undefined) ||
                         (user.role === "landlord" && d.landlord_accepted !== undefined);
    return (
      <div className="space-y-4" data-testid="pane-settlement">
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
          <div className="flex items-center gap-2 text-base font-semibold text-indigo-800">
            🤖 ORE-AI Decision
            {d.ore_ai_confidence && (
              <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600">
                {d.ore_ai_confidence} confidence
              </span>
            )}
          </div>
          <p className="text-sm text-indigo-700 mt-2 leading-relaxed">{d.orello_suggestion}</p>
        </div>
        <div className="card-base p-5 space-y-3">
          <div className="text-sm font-semibold">Proposed settlement</div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Landlord keeps</span>
            <span className="font-medium text-red-600">{formatINR(disputedAmount)}</span>
          </div>
          <div className="flex justify-between text-sm border-t pt-2">
            <span className="font-semibold">Tenant refund</span>
            <span className="font-bold text-emerald-600">{formatINR(refundAmount)}</span>
          </div>
        </div>
        {!hasResponded && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">Do you accept Orello's decision?</div>
            <div className="flex gap-3">
              <button onClick={acceptOrelloDecision} disabled={busy}
                className="bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                ✓ Accept Decision
              </button>
              <button onClick={goExternal} disabled={busy}
                className="border border-red-300 text-red-600 hover:bg-red-50 px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
                ⚖️ Take Externally
              </button>
            </div>
            <div className="text-xs text-gray-400">
              If you choose external resolution, the disputed amount of {formatINR(disputedAmount)} will remain held by Orello until a court or external authority provides a decision. The undisputed amount of {formatINR(refundAmount)} will be released to the tenant immediately.
            </div>
          </div>
        )}
        {hasResponded && (
          <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            Your response has been recorded. Waiting for the other party.
          </div>
        )}
      </div>
    );
  }

  // ── Partial settlement — dispute in progress ──
  if (s?.status === "partial") {
    const isExternal = r.status === "external_dispute";
    return (
      <div className="space-y-4" data-testid="pane-settlement">
        <div className={`rounded-xl p-5 ${isExternal ? "bg-gray-50 border border-gray-200" : "bg-amber-50 border border-amber-200"}`}>
          <div className={`font-semibold ${isExternal ? "text-gray-800" : "text-amber-800"}`}>
            {isExternal ? "⚖️ External Dispute — Partial Settlement" : "🏛 Orello Dispute — Partial Settlement"}
          </div>
          <p className={`text-sm mt-1 ${isExternal ? "text-gray-600" : "text-amber-700"}`}>
            {isExternal
              ? "Disputed amount held by Orello pending external resolution. Agreed amounts have been processed."
              : "Orello is reviewing the dispute. Agreed amounts released, disputed amount held safely."}
          </p>
        </div>
        <div className="card-base p-5 space-y-3">
          <div className="text-sm font-semibold text-gray-700">Deposit breakdown</div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">Total deposit held</span><span>{formatINR(r.deposit_amount)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">Released to tenant</span><span className="text-emerald-600 font-medium">{formatINR(s.tenant_refund_amount)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">Agreed deduction to landlord</span><span className="text-red-500">− {formatINR(s.landlord_deduction_amount)}</span></div>
          <div className="flex justify-between text-sm font-semibold border-t pt-2">
            <span className="text-amber-700">Held by Orello (disputed)</span>
            <span className="text-amber-600">{formatINR(s.disputed_amount_held)}</span>
          </div>
        </div>
        {s.deduction_reason && (
          <div className="card-base p-4 text-sm text-gray-600">{s.deduction_reason}</div>
        )}
      </div>
    );
  }

  // ── Deduction agreed, no dispute — waiting for release ──
  if (deduction?.final_status === "agreed" && !d) {
    return (
      <div className="space-y-4" data-testid="pane-settlement">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="font-semibold text-emerald-700">Deductions agreed by both parties</div>
          <p className="text-sm text-emerald-600 mt-1">No dispute raised. Settlement being processed.</p>
        </div>
        <div className="card-base p-5 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-gray-500">Total deposit</span><span>{formatINR(r.deposit_amount)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-gray-500">Agreed deduction</span><span className="text-red-600">− {formatINR(deduction.total_deduction)}</span></div>
          <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Tenant refund</span><span className="text-emerald-600">{formatINR(deduction.tenant_refund)}</span></div>
        </div>
      </div>
    );
  }

  // ── Nothing yet ──
  return (
    <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg" data-testid="pane-settlement">
      Settlement will appear here after the checkout and deduction process is complete.
    </div>
  );
}

// ─── Ratings Pane ─────────────────────────────────────────────────────────────

function RatingsPane({ r, onDone }) {
  const { user } = useAuth();

  // Hooks must be at the top level — before any early returns
  const [picked, setPicked] = useState(null);
  const [scores, setScores] = useState({ communication_score: 0, professionalism_score: 0, fairness_score: 0, overall_score: 0, notes: "" });

  // PHASE 9: New rating system
  if (r.status === "rating_phase" || r.status === "archived") {
    return <Phase9RatingsPane r={r} user={user} onDone={onDone} />;
  }

  // Legacy phase1/phase2 rating system
  const phase = r.status === "rating_phase2" || r.status === "completed" ? "phase2" : "phase1";
  const others = [];
  if (user.role !== "tenant" && r.tenants_resolved?.[0]) others.push({ id: r.tenants_resolved[0].tenant_id, name: r.tenants_resolved[0].user?.name, role: "tenant" });
  if (user.role !== "landlord" && r.landlord) others.push({ id: r.landlord_id, name: r.landlord.name, role: "landlord" });

  const submit = async () => {
    if (!picked || !scores.overall_score) { toast.error("Please select who you want to rate and give at least an overall score."); return; }
    try {
      await api.post(`/rentals/${r.id}/ratings`, { phase, to_user_id: picked, ...scores });
      toast.success("Thanks! Your rating has been submitted.");
      setPicked(null);
      setScores({ communication_score: 0, professionalism_score: 0, fairness_score: 0, overall_score: 0, notes: "" });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const myRatings = (r.ratings || []).filter(x => x.from_user_id === user.id && x.phase === phase);

  return (
    <div className="space-y-5" data-testid="pane-ratings">
      <div>
        <h3 className="text-lg font-semibold font-display">{"How was your experience?"}</h3>
        <p className="text-sm text-gray-500">{phase === "phase1" ? "Your honest feedback helps build trust on Orello." : "Help others know who they are renting with."}</p>
      </div>
      {others.map(o => {
        const done = myRatings.find(x => x.to_user_id === o.id);
        return (
          <div key={o.id} className="card-base p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{o.name}</div>
                <div className="text-xs text-gray-500 capitalize">{o.role}</div>
              </div>
              {done ? <span className="pill bg-emerald-50 text-emerald-700">Submitted</span>
                : <button onClick={() => setPicked(o.id)} className="text-sm text-[#F97316]">Leave a review →</button>}
            </div>
            {picked === o.id && (
              <div className="mt-4 space-y-3 border-t pt-4">
                <div className="flex items-center justify-between"><span className="text-sm">Communication</span><Stars value={scores.communication_score} onChange={(v) => setScores({ ...scores, communication_score: v })} /></div>
                <div className="flex items-center justify-between"><span className="text-sm">Professionalism</span><Stars value={scores.professionalism_score} onChange={(v) => setScores({ ...scores, professionalism_score: v })} /></div>
                {phase === "phase2" && <div className="flex items-center justify-between"><span className="text-sm">Fairness</span><Stars value={scores.fairness_score} onChange={(v) => setScores({ ...scores, fairness_score: v })} /></div>}
                <div className="flex items-center justify-between"><span className="text-sm">Overall</span><Stars value={scores.overall_score} onChange={(v) => setScores({ ...scores, overall_score: v })} /></div>
                <textarea value={scores.notes} onChange={(e) => setScores({ ...scores, notes: e.target.value })} placeholder="Share your experience — what went well, what could be better." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
                <button onClick={submit} className="bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-5 py-2 rounded-lg text-sm font-medium">Submit Rating</button>
              </div>
            )}
          </div>
        );
      })}
      {r.status === "completed" && (
        <div className="text-sm bg-emerald-50 text-emerald-700 p-4 rounded-lg">🎉 Deal closed. Thank you for using Orello.</div>
      )}
    </div>
  );
}
// ─── Landlord Payout Widget ───────────────────────────────────────────────────
function LandlordPayoutWidget({ rid, amount }) {
  const { token, user } = useAuth();
  const [upi, setUpi] = useState("");
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    fetch("/api/wallet", {
      headers: { Authorization: `Bearer ${localStorage.getItem("orello_token")}` },
    })
      .then(r => r.json())
      .then(d => setBalance(d.balance_paise || 0))
      .catch(() => {});
  }, []);

  const request = async () => {
    if (!upi || !upi.includes("@")) {
      toast.error("Enter a valid UPI ID like name@upi");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/rentals/${rid}/landlord/request-payout`, {
        amount_paise: amount,
        upi_id: upi,
      });
      toast.success(`₹${(amount/100).toLocaleString("en-IN")} sent to ${upi}`);
      setPaid(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (paid) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0" />
        <div>
          <div className="text-sm font-semibold text-emerald-700">Payout initiated</div>
          <div className="text-xs text-emerald-600 mt-0.5">Arrives in 1–2 business days to {upi}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-base p-5 space-y-4 border-orange-200">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-orange-50 flex items-center justify-center">
          <span className="text-base">💸</span>
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-800">Receive your deduction amount</div>
          <div className="text-xs text-gray-500">
            {formatINR(amount)} will be sent to your UPI
          </div>
        </div>
      </div>

      {user?.kyc_status !== "verified" && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ Complete KYC verification to receive payouts
        </div>
      )}

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Your UPI ID</label>
        <input
          value={upi}
          onChange={e => setUpi(e.target.value)}
          placeholder="yourname@upi"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#F97316]"
        />
        <p className="text-xs text-gray-400 mt-1">e.g. name@okicici · name@paytm · name@ybl</p>
      </div>

      <button
        onClick={request}
        disabled={busy || user?.kyc_status !== "verified"}
        className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? "Processing..." : `Receive ${formatINR(amount)}`}
      </button>
      <p className="text-xs text-gray-400 text-center">Processed within 1–2 business days via RazorpayX</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// PHASE 8: Refund Claim Component
// ────────────────────────────────────────────────────────────────
function TenantRefundClaimWidget({ rid, amount, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    account_type: "savings",
  });
  const { user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Name is fixed to the KYC-verified identity (anti-fraud) — always submit user.name.
    const payload = { ...form, account_holder_name: user?.name || "" };
    if (!payload.account_holder_name || !payload.account_number.trim() || !payload.ifsc_code.trim()) {
      toast.error("Please fill account number and IFSC code.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/rentals/${rid}/claim-refund`, payload);
      toast.success(`Refund of ${formatINR(amount)} sent via Cashfree ${data?.transfer_mode || "Payouts"} — arriving ${data?.transfer_eta || "shortly"}.`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-base p-5 border border-emerald-200 bg-emerald-50">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-4">
        <Check className="h-5 w-5" /> Claim Your Refund
      </div>
      <p className="text-sm text-emerald-600 mb-4">Claim your refund of {formatINR(amount)} anytime by providing your bank details. If you need help, <a href="https://wa.me/911234567890" className="underline font-medium" target="_blank" rel="noopener noreferrer">contact support</a>.</p>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Auto-filled from KYC */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">Account Holder Name</label>
          <input
            type="text"
            readOnly
            value={user?.name || ""}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
          />
          <p className="text-xs text-gray-400 mt-1">Auto-filled from your KYC — the refund can only go to an account in your verified name.</p>
        </div>

        {/* Account Number */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">Account Number *</label>
          <input
            type="text"
            placeholder="e.g., 12345678901234"
            value={form.account_number}
            onChange={(e) => setForm({ ...form, account_number: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* IFSC Code */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">IFSC Code *</label>
          <input
            type="text"
            placeholder="e.g., HDFC0000001"
            value={form.ifsc_code}
            onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* Account Type */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">Account Type</label>
          <select
            value={form.account_type}
            onChange={(e) => setForm({ ...form, account_type: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="savings">Savings</option>
            <option value="current">Current</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 transition-colors"
        >
          {busy ? "Processing..." : `Claim ${formatINR(amount)}`}
        </button>
      </form>

      <p className="text-xs text-gray-400 text-center mt-3">Sent automatically via Cashfree Payouts — no manual step. IMPS transfers (up to ₹5L) arrive within minutes; larger amounts go by NEFT (~2 hours).</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// PHASE 9: Ratings Pane (New System)
// ────────────────────────────────────────────────────────────────
function Phase9RatingsPane({ r, user, onDone }) {
  const isBrokerCreated = r.created_by_role === "broker_admin" || r.created_by_role === "agent";

  // Determine who to rate
  let toRate = [];
  if (isBrokerCreated) {
    // Tenant rates broker/agent, landlord rates broker/agent
    if (user.role === "tenant") {
      // Find broker/agent
      if (r.agent_id) {
        const agent = r.agent || { id: r.agent_id, name: "Agent" };
        toRate.push({ id: agent.id, name: agent.name, role: "agent" });
      }
      if (r.broker_id) {
        const broker = r.broker || { id: r.broker_id, name: "Broker" };
        toRate.push({ id: broker.id, name: broker.name, role: "broker" });
      }
    } else if (user.role === "landlord") {
      if (r.agent_id) {
        const agent = r.agent || { id: r.agent_id, name: "Agent" };
        toRate.push({ id: agent.id, name: agent.name, role: "agent" });
      }
      if (r.broker_id) {
        const broker = r.broker || { id: r.broker_id, name: "Broker" };
        toRate.push({ id: broker.id, name: broker.name, role: "broker" });
      }
    }
  } else {
    // Self-managed: tenant rates landlord, landlord rates tenant
    if (user.role === "tenant" && r.landlord_id) {
      const landlord = r.landlord || { id: r.landlord_id, name: "Landlord" };
      toRate.push({ id: landlord.id, name: landlord.name, role: "landlord" });
    } else if (user.role === "landlord" && r.tenants?.[0]?.tenant_id) {
      const tenant = r.tenants[0].user || { id: r.tenants[0].tenant_id, name: "Tenant" };
      toRate.push({ id: tenant.id, name: tenant.name, role: "tenant" });
    }
  }

  return (
    <div className="space-y-5" data-testid="pane-ratings-phase9">
      <div>
        <h3 className="text-lg font-semibold font-display">Share Your Experience</h3>
        <p className="text-sm text-gray-500">Help others make informed decisions. Your honest feedback is valuable.</p>
      </div>

      {r.status === "archived" ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <Check className="h-5 w-5" /> Rental Complete
          </div>
          <p className="text-sm text-emerald-600 mt-1">Both parties have rated each other. Thank you for using Orello!</p>
        </div>
      ) : (
        toRate.map(person => (
          <div key={person.id} className="card-base p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-semibold text-gray-800">{person.name}</div>
                <div className="text-xs text-gray-500 capitalize">{person.role}</div>
              </div>
              <span className="text-xs text-gray-400">Rate them</span>
            </div>
            <FinalRatingWidget rid={r.id} toUserId={person.id} toUserRole={person.role} onDone={onDone} />
          </div>
        ))
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// PHASE 9: Final Rating Widget Component
// ────────────────────────────────────────────────────────────────
function FinalRatingWidget({ rid, toUserId, toUserRole, onDone }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState({
    communication_score: 0,
    professionalism_score: 0,
    fairness_score: 0,
    overall_score: 0,
    notes: "",
    would_recommend: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating.overall_score) {
      toast.error("Please provide an overall rating");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/rentals/${rid}/rating`, {
        ...rating,
        to_user_id: toUserId,
        to_user_role: toUserRole,
      });
      toast.success("Rating submitted successfully!");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card-base p-5 space-y-4">
      <div className="text-sm font-semibold text-gray-700">Rate Your Experience</div>

      {/* Communication */}
      <div>
        <label className="text-xs font-medium text-gray-700 block mb-2">Communication</label>
        <Stars value={rating.communication_score} onChange={(v) => setRating({ ...rating, communication_score: v })} />
      </div>

      {/* Professionalism */}
      <div>
        <label className="text-xs font-medium text-gray-700 block mb-2">Professionalism</label>
        <Stars value={rating.professionalism_score} onChange={(v) => setRating({ ...rating, professionalism_score: v })} />
      </div>

      {/* Fairness */}
      <div>
        <label className="text-xs font-medium text-gray-700 block mb-2">Fairness (Mandatory)</label>
        <Stars value={rating.fairness_score} onChange={(v) => setRating({ ...rating, fairness_score: v })} />
      </div>

      {/* Overall */}
      <div>
        <label className="text-xs font-medium text-gray-700 block mb-2">Overall Rating *</label>
        <Stars value={rating.overall_score} onChange={(v) => setRating({ ...rating, overall_score: v })} />
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs font-medium text-gray-700 block mb-1">Notes (optional)</label>
        <textarea
          placeholder="Share your experience..."
          value={rating.notes}
          onChange={(e) => setRating({ ...rating, notes: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
          rows={3}
        />
      </div>

      {/* Would Recommend */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={rating.would_recommend}
          onChange={(e) => setRating({ ...rating, would_recommend: e.target.checked })}
          className="rounded"
        />
        <label className="text-sm text-gray-700">I would recommend this person</label>
      </div>

      <button
        type="submit"
        disabled={busy || !rating.overall_score}
        className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 transition-colors"
      >
        {busy ? "Submitting..." : "Submit Rating"}
      </button>
    </form>
  );
}
